import { directSupabase } from '../../../lib/supabaseClient';
import type { Document, DocumentInput, DocumentLine, DocumentLineInput, DocumentWithLines, DocumentFeesConfig, DocumentKind } from '../types/documents.types';
import { computeTotals } from '../types/documents.types';
import { visualDesignerSurveysService } from './visualDesignerSurveys.service';
import { assertCanAssignDocumentToProject } from '../../../services/documentAssignment';
import type { QuoteLikeVersion } from '../utils/versionPairings';
import { isMissingRpcError } from '../utils/missingRpc';
import { emitActivityEventBackground } from '../../../services/activityEvents.service';
import {
  computeInvoiceDelta,
  depositInvoiceLineName,
  depositInvoiceNarrative,
  formatInvoiceRef,
  invoiceAmountEditable,
  invoiceCanBeLifecycleSynced,
  deductionAmount,
  deductionLineNets,
  invoiceRoleLabel,
  isDeductionLine,
  nextInvoiceSequence,
  normalizeInvoiceRole,
  reissuedFromInvoiceId,
  resolveQuotedGrossForDeposit,
  resolveSourceVat,
  type InvoiceRole,
} from '../utils/invoiceLedger';
import { defaultPaymentTermsInDays, invoicePaymentScheduleLocked } from '../utils/invoicePaymentTerms';
import {
  QUOTE_STATUS_REVERT_BLOCKED_MESSAGE,
  finalSurveyWorkPhase,
  quoteStatusRevertBlocked,
} from '../utils/surveySheetDelete';

async function currentUserId(): Promise<string | null> {
  try {
    const { data } = await directSupabase.auth.getUser();
    return data.user?.id || null;
  } catch {
    return null;
  }
}

function docActivityNoun(kind?: string | null): string {
  const k = String(kind || '').toLowerCase();
  if (k === 'invoice') return 'Invoice';
  if (k === 'estimate') return 'Estimate';
  if (k === 'quotation') return 'Quote';
  return 'Document';
}

function getDocumentStatusTimestampPatch(
  nextStatus: string,
  previousStatus?: string | null
): Record<string, string> {
  const nowIso = new Date().toISOString();
  if (previousStatus != null && previousStatus === nextStatus) return {};
  const patch: Record<string, string> = { status_changed_at: nowIso };
  const normalized = String(nextStatus || '').trim().toLowerCase();
  if (normalized === 'draft') patch.draft_at = nowIso;
  if (normalized === 'sent') patch.sent_at = nowIso;
  if (normalized === 'accepted') patch.accepted_at = nowIso;
  if (normalized === 'rejected') patch.rejected_at = nowIso;
  if (normalized === 'invoiced') patch.invoiced_at = nowIso;
  if (normalized === 'paid') patch.paid_at = nowIso;
  if (normalized === 'cash') patch.paid_at = nowIso;
  if (normalized === 'void') patch.voided_at = nowIso;
  return patch;
}

function toDbDocument(input: DocumentInput) {
  const statusValue = input.status ?? 'draft';
  return {
    source: input.source ?? 'jarvis',
    kind: input.kind,
    lifecycle_ref: input.lifecycleRef,
    ref_no: input.kind === 'Invoice' && input.invoiceRef ? input.invoiceRef : input.lifecycleRef,
    organisation_id: input.organisationId,
    project_id: input.projectId ?? null,
    customer_id: input.customerId ?? null,
    ...(input.accountId ? { account_id: input.accountId } : {}),
    ...(input.vatTreatment ? { vat_treatment: input.vatTreatment } : {}),
    quotedock_customer_id: input.quotedockCustomerId ?? null,
    customer_first_name: input.customerFirstName ?? null,
    customer_last_name: input.customerLastName ?? null,
    customer_title: input.customerTitle ?? null,
    title: input.title,
    description: input.description ?? null,
    notes: input.notes ?? null,
    status: statusValue,
    currency: input.currency ?? 'GBP',
    deposit_amount: input.depositAmount ?? 0,
    deposit_percentage: input.depositPercentage ?? null,
    has_deposit: input.hasDeposit ?? false,
    project_days: input.projectDays ?? null,
    project_hours: input.projectHours ?? null,
    vat_enabled: input.vatEnabled ?? true,
    vat_pct: input.vatPct ?? 0.2,
    input_vat_selected: input.inputVatSelected ?? false,
    issued_at: input.issuedAt ?? null,
    ...(typeof input.paymentTermsInDays === 'number' ? { payment_terms_in_days: input.paymentTermsInDays } : {}),
    is_original: input.isOriginal ?? false,
    payment_structure_applicable: input.paymentStructureApplicable ?? false,
    payment_structure_pattern: input.paymentStructurePattern ?? null,
    invoice_role: input.invoiceRole ?? null,
    frozen_at: input.frozenAt ?? null,
    source_survey_event: input.sourceSurveyEvent ?? null,
    invoice_ref: input.invoiceRef ?? null,
    admin_pct: input.fees.adminPct,
    admin_cap: input.fees.adminCap,
    contingency_pct: input.fees.contingencyPct,
    profit_pct: input.fees.profitPct,
    developer_pct: null,
    ...getDocumentStatusTimestampPatch(statusValue, null),
  };
}

function toDbLine(documentId: string, line: DocumentLineInput) {
  const lineTotal = line.lineTotal != null ? line.lineTotal : (line.qty || 0) * (line.unitPrice || 0);
  return {
    document_id: documentId,
    line_kind: line.lineKind,
    additional_type: line.additionalType ?? null,
    material_id: line.materialId ?? null,
    equipment_id: line.equipmentId ?? null,
    labour_rate_id: line.labourRateId ?? null,
    name: line.name,
    description: line.description ?? null,
    qty: line.qty,
    unit: line.unit ?? null,
    unit_price: line.unitPrice,
    vat_inclusive: !!line.vatInclusive,
    line_total: lineTotal,
    sort_order: line.sortOrder ?? null,
    labour_days: line.labourDays ?? null,
    labour_hours: line.labourHours ?? null,
    meta_json: (line as any).metaJson ?? null,
  };
}

function isDesignRow(row: any): boolean {
  return String(row?.meta_json?.feature || '').trim() === 'visual_designer';
}

/** Replaces a version's lines in one database transaction (keepDesigns: commercial lines only). */
async function writeVersionLines(versionId: string, payload: any[], keepDesigns: boolean): Promise<void> {
  const { error } = await directSupabase.rpc('replace_version_lines', {
    p_version_id: versionId,
    p_lines: payload,
    p_keep_designs: keepDesigns,
  });
  if (!error) return;
  if (!isMissingRpcError(String(error.message || ''))) throw new Error(error.message);

  let rows = payload;
  if (keepDesigns) {
    const { data: existing, error: exErr } = await directSupabase
      .from('document_version_lines')
      .select('id, sort_order, meta_json')
      .eq('version_id', versionId);
    if (exErr) throw new Error(exErr.message);
    const designs = (existing || []).filter(isDesignRow);
    const replaceIds = (existing || []).filter((row: any) => !isDesignRow(row)).map((row: any) => row.id);
    if (replaceIds.length > 0) {
      const { error: delErr } = await directSupabase.from('document_version_lines').delete().in('id', replaceIds);
      if (delErr) throw new Error(delErr.message);
    }
    const nextSort = designs.reduce((max: number, row: any) => Math.max(max, Number(row.sort_order ?? -1)), -1) + 1;
    rows = payload
      .filter((row) => !isDesignRow(row))
      .map((row, idx) => ({ ...row, sort_order: nextSort + idx }));
  } else {
    const { error: delErr } = await directSupabase.from('document_version_lines').delete().eq('version_id', versionId);
    if (delErr) throw new Error(delErr.message);
  }
  if (rows.length === 0) return;
  const { error: insErr } = await directSupabase.from('document_version_lines').insert(rows);
  if (insErr) throw new Error(insErr.message);
}

/** Replaces a document's base lines in one database transaction and returns the saved rows. */
async function writeDocumentLines(documentId: string, payload: any[]): Promise<any[]> {
  const { data, error } = await directSupabase.rpc('replace_document_lines', {
    p_document_id: documentId,
    p_lines: payload,
  });
  if (!error) return (data as any[]) || [];
  if (!isMissingRpcError(String(error.message || ''))) throw new Error(error.message);

  const { error: delErr } = await directSupabase.from('document_lines').delete().eq('document_id', documentId);
  if (delErr) throw new Error(delErr.message);
  if (payload.length === 0) return [];
  const { data: inserted, error: insErr } = await directSupabase.from('document_lines').insert(payload).select('*');
  if (insErr) throw new Error(insErr.message);
  return inserted || [];
}

async function unlinkOtherQuotationsFromProject(projectId: string, keepDocumentId: string) {
  const linkedProjectId = String(projectId || '').trim();
  const keepId = String(keepDocumentId || '').trim();
  if (!linkedProjectId || !keepId) return;
  const { error } = await directSupabase
    .from('documents')
    .update({ project_id: null })
    .eq('project_id', linkedProjectId)
    .eq('kind', 'Quotation')
    .neq('id', keepId);
  if (error) throw new Error(error.message);
}

function fromDbDocument(row: any): Document {
  return {
    id: row.id,
    source: row.source,
    kind: row.kind,
    lifecycleRef: row.lifecycle_ref,
    organisationId: row.organisation_id,
    projectId: row.project_id,
    customerId: row.customer_id,
    accountId: row.account_id ?? null,
    vatTreatment: row.vat_treatment ?? null,
    quotedockCustomerId: row.quotedock_customer_id,
    customerFirstName: row.customer_first_name,
    customerLastName: row.customer_last_name,
    customerTitle: row.customer_title,
    title: row.title,
    description: row.description,
    notes: row.notes ?? null,
    status: row.status,
    currency: row.currency,
    activeVersionId: row.active_version_id ?? null,
    depositAmount: Number(row.deposit_amount ?? 0),
    depositPercentage: row.deposit_percentage != null ? Number(row.deposit_percentage) : null,
    hasDeposit: !!row.has_deposit,
    projectDays: row.project_days != null ? Number(row.project_days) : null,
    projectHours: row.project_hours != null ? Number(row.project_hours) : null,
    vatEnabled: !!row.vat_enabled,
    vatPct: Number(row.vat_pct ?? 0.2),
    inputVatSelected: !!row.input_vat_selected,
    issuedAt: row.issued_at,
    paymentTermsInDays: row.payment_terms_in_days != null ? Number(row.payment_terms_in_days) : null,
    isOriginal: !!row.is_original,
    fees: {
      adminPct: Number(row.admin_pct ?? 0),
      adminCap: Number(row.admin_cap ?? 0),
      contingencyPct: Number(row.contingency_pct ?? 0),
      profitPct: Number(row.profit_pct ?? 0),
      developerPct: null,
    },
    createdBy: row.created_by,
    updatedBy: row.updated_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    statusChangedAt: row.status_changed_at ?? null,
    draftAt: row.draft_at ?? null,
    sentAt: row.sent_at ?? null,
    acceptedAt: row.accepted_at ?? null,
    rejectedAt: row.rejected_at ?? null,
    invoicedAt: row.invoiced_at ?? null,
    paidAt: row.paid_at ?? null,
    voidedAt: row.voided_at ?? null,
    netTotal: row.net_total != null ? Number(row.net_total) : undefined,
    vatAmount: row.vat_amount != null ? Number(row.vat_amount) : undefined,
    grossTotal: row.gross_total != null ? Number(row.gross_total) : undefined,
    paymentStructureApplicable: !!row.payment_structure_applicable,
    paymentStructurePattern: row.payment_structure_pattern ?? null,
    invoiceRole: row.invoice_role ?? null,
    frozenAt: row.frozen_at ?? null,
    sourceSurveyEvent: row.source_survey_event ?? null,
    invoiceRef: row.invoice_ref ?? null,
  } as Document;
}

function fromDbLine(row: any): DocumentLine {
  return {
    id: row.id,
    documentId: row.document_id,
    lineKind: row.line_kind,
    additionalType: row.additional_type ?? null,
    materialId: row.material_id,
    equipmentId: row.equipment_id,
    labourRateId: row.labour_rate_id,
    name: row.name,
    description: row.description,
    qty: Number(row.qty ?? 0),
    unit: row.unit,
    unitPrice: Number(row.unit_price ?? 0),
    lineTotal: Number(row.line_total ?? 0),
    vatInclusive: !!row.vat_inclusive,
    sortOrder: row.sort_order,
    labourDays: row.labour_days,
    labourHours: row.labour_hours,
    metaJson: row.meta_json ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  } as DocumentLine;
}

/**
 * Calculate and store totals in the database
 * This ensures net_total, vat_amount, gross_total, and deposit_amount are always up-to-date
 */
async function calculateAndStoreTotals(
  documentId: string,
  lines: DocumentLine[],
  fees: DocumentFeesConfig,
  vatEnabled: boolean,
  vatPct: number
): Promise<void> {
  const totals = computeTotals(
    lines.map(l => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal, vatInclusive: l.vatInclusive })),
    fees,
    { enabled: vatEnabled, pct: vatPct }
  );

  // Calculate net total (subtotal before VAT) = preProfit + profit
  const netTotal = totals.preProfit + totals.profit;
  
  // Calculate VAT amount = netTotal * vatPct (when VAT enabled)
  const vatAmount = vatEnabled ? netTotal * vatPct : 0;
  
  // Gross total = netTotal + vatAmount (or just grandTotal from computed totals)
  const grossTotal = totals.grandTotal;

  // Get document to check deposit_percentage and has_deposit
  const { data: doc, error: docErr } = await directSupabase
    .from('documents')
    .select('deposit_percentage, has_deposit, deposit_amount, invoice_role, kind')
    .eq('id', documentId)
    .maybeSingle(); // Use maybeSingle() instead of single() to handle missing documents gracefully
  
  if (docErr) {
    console.error('Error fetching document for totals calculation:', docErr);
    throw new Error(`Failed to fetch document ${documentId}: ${docErr.message}`);
  }
  
  if (!doc) {
    console.warn(`Document ${documentId} not found when calculating totals`);
    // Still update totals even if we can't get deposit info
    // This handles edge cases where document might not exist yet
  }

  // Store in database
  const updatePayload: any = {
    net_total: netTotal,
    vat_amount: vatAmount,
    gross_total: grossTotal,
  };

  // Recalculate deposit_amount if deposit_percentage is set (percentage mode)
  // Fixed mode deposits are set directly and shouldn't be recalculated.
  // Role invoices already store the cash amount — do not take a % of themselves.
  const isRoleInvoice = String(doc?.kind || '') === 'Invoice' && !!normalizeInvoiceRole(doc?.invoice_role);
  if (doc && doc.has_deposit && doc.deposit_percentage != null && !isRoleInvoice) {
    // Percentage mode: calculate from gross total
    const depositAmount = Math.round((grossTotal * doc.deposit_percentage + Number.EPSILON) * 100) / 100;
    updatePayload.deposit_amount = depositAmount;
  }
  // If has_deposit is true but deposit_percentage is null, it's fixed mode - don't recalculate

  await directSupabase
    .from('documents')
    .update(updatePayload)
    .eq('id', documentId);
}

/**
 * The document row mirrors its current First/Final Survey pricing record.
 * Run before `calculateAndStoreTotals`, which reads the deposit settings from the document row.
 */
async function syncDocumentPricingFromVersion(documentId: string, versionHeader: any): Promise<void> {
  const { error } = await directSupabase
    .from('documents')
    .update({
      admin_pct: versionHeader.admin_pct,
      admin_cap: versionHeader.admin_cap,
      contingency_pct: versionHeader.contingency_pct,
      profit_pct: versionHeader.profit_pct,
      vat_enabled: !!versionHeader.vat_enabled,
      vat_pct: versionHeader.vat_pct,
      input_vat_selected: !!versionHeader.input_vat_selected,
      has_deposit: !!versionHeader.has_deposit,
      deposit_amount: versionHeader.deposit_amount,
      deposit_percentage: versionHeader.deposit_percentage,
    })
    .eq('id', documentId);
  if (error) throw new Error(error.message);
}

function roundMoney(value: number): number {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function getComputedSnapshot(lines: DocumentLine[], fees: DocumentFeesConfig, vatEnabled: boolean, vatPct: number) {
  const totals = computeTotals(
    lines.map(l => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal, vatInclusive: l.vatInclusive })),
    fees,
    { enabled: vatEnabled, pct: vatPct }
  );
  const netTotal = roundMoney(totals.preProfit + totals.profit);
  const vatAmount = roundMoney(vatEnabled ? netTotal * vatPct : 0);
  const grossTotal = roundMoney(totals.grandTotal);
  return { totals, netTotal, vatAmount, grossTotal };
}

async function refreshShareLinkSnapshotsForVersion(
  versionId: string,
  lines: DocumentLine[],
  fees: DocumentFeesConfig,
  vatEnabled: boolean,
  vatPct: number
): Promise<void> {
  const snapshot = getComputedSnapshot(lines, fees, vatEnabled, vatPct);
  const { error } = await directSupabase
    .from('document_share_links')
    .update({
      sent_net_total: snapshot.netTotal,
      sent_vat_amount: snapshot.vatAmount,
      sent_gross_total: snapshot.grossTotal,
    })
    .eq('sent_version_id', versionId);
  if (error) throw new Error(error.message);
}

export const documentsService = {
  async listOrganisations(): Promise<Array<{ id: string; name: string; code: string }>> {
    const { data, error } = await directSupabase.from('organisations').select('id, name, code').order('name', { ascending: true });
    if (error) throw new Error(error.message);
    return (data || []) as Array<{ id: string; name: string; code: string }>;
  },

  async getSequencePreview(organisationId: string): Promise<string> {
    const { data, error } = await directSupabase
      .from('doc_sequences')
      .select('prefix, next_number, pad_length')
      .eq('organisation_id', organisationId)
      .eq('is_active', true)
      .maybeSingle(); // Use maybeSingle() to handle case where no sequence exists
    if (error) throw new Error(error.message);
    if (!data) {
      throw new Error(`No active sequence found for organisation ${organisationId}`);
    }
    const prefix: string = data?.prefix || '';
    const next: number = data?.next_number || 1;
    const pad: number = data?.pad_length || 4;
    const padded = String(next).padStart(pad, '0');
    return `${prefix}${padded}`;
  },

  async refExists(organisationId: string, kind: DocumentKind, lifecycleRef: string): Promise<boolean> {
    const { data, error } = await directSupabase
      .from('documents')
      .select('id')
      .eq('organisation_id', organisationId)
      .eq('kind', kind)
      .eq('lifecycle_ref', lifecycleRef)
      .limit(1);
    if (error) throw new Error(error.message);
    return Array.isArray(data) && data.length > 0;
  },

  async mintLifecycleRef(organisationId: string): Promise<string> {
    const { data, error } = await (directSupabase as any).rpc('mint_lifecycle_ref', { org_id: organisationId });
    if (error) throw new Error(error.message);
    return data as string;
  },

  async mintInvoiceRef(organisationId: string, lifecycleRef: string): Promise<string> {
    const jobRef = String(lifecycleRef || '').trim();
    if (!organisationId || !jobRef) {
      throw new Error('Invoice reference requires organisation and job reference');
    }
    const { data, error } = await (directSupabase as any).rpc('mint_invoice_ref', {
      org_id: organisationId,
      job_ref: jobRef,
    });
    if (!error && data) return String(data).trim();
    const existing = await this.listLifecycleInvoices(organisationId, jobRef);
    return formatInvoiceRef(jobRef, nextInvoiceSequence(existing.map((row) => row.invoiceRef), jobRef));
  },

  async createDocument(input: DocumentInput, lines: DocumentLineInput[]): Promise<DocumentWithLines> {
    // Guard: every document is addressed to a customer, or (invoices only) a directory account
    if (!input.customerId && !(input.kind === 'Invoice' && input.accountId)) {
      throw new Error('Customer is required');
    }
    // Only mint a new lifecycleRef if one doesn't exist
    if (!input.lifecycleRef) {
      input.lifecycleRef = await this.mintLifecycleRef(input.organisationId);
    }
    if (input.kind === 'Invoice' && !String(input.invoiceRef || '').trim()) {
      input.invoiceRef = await this.mintInvoiceRef(input.organisationId, input.lifecycleRef);
    }
    const payload = toDbDocument(input);
    const userId = await currentUserId();
    if (userId) {
      (payload as any).created_by = userId;
      (payload as any).updated_by = userId;
    }
    let { data, error } = await directSupabase
      .from('documents')
      .insert(payload)
      .select('*')
      .maybeSingle(); // Use maybeSingle() to handle edge cases
    if (error && /invoice_ref/.test(error.message)) {
      const fallback = { ...payload } as Record<string, unknown>;
      delete fallback.invoice_ref;
      ({ data, error } = await directSupabase
        .from('documents')
        .insert(fallback)
        .select('*')
        .maybeSingle());
    }
    if (error && /payment_terms_in_days|schema cache/i.test(error.message)) {
      const fallback = { ...payload } as Record<string, unknown>;
      delete fallback.payment_terms_in_days;
      ({ data, error } = await directSupabase
        .from('documents')
        .insert(fallback)
        .select('*')
        .maybeSingle());
    }
    if (error) throw new Error(error.message);
    if (!data) {
      throw new Error('Failed to create document: insert returned no data');
    }

    const document = fromDbDocument(data);
    if (document.projectId) {
      emitActivityEventBackground({
        action: 'document.created',
        entityType: 'document',
        entityId: document.id,
        projectId: document.projectId,
        actorId: userId,
        label: `${docActivityNoun(document.kind)} created${document.invoiceRef || document.lifecycleRef ? ` (${document.invoiceRef || document.lifecycleRef})` : ''}`,
        metadata: { kind: document.kind, status: document.status, lifecycleRef: document.lifecycleRef, invoiceRef: document.invoiceRef },
      });
    }
    let mapped: DocumentLine[] = [];
    if (lines && lines.length > 0) {
      const linePayloads = lines.map(l => toDbLine(document.id, l));
      const { data: lineRows, error: lineErr } = await directSupabase
        .from('document_lines')
        .insert(linePayloads)
        .select('*');
      if (lineErr) throw new Error(lineErr.message);
      mapped = (lineRows || []).map(fromDbLine);
    }
    // Versioning: skip for Invoices
    if (document.kind !== 'Invoice') {
      // Auto-create Version 1 and set active
      const { data: v1, error: vErr } = await directSupabase
        .from('document_versions')
        .insert({
          document_id: document.id,
          version_number: 1,
          is_active: true,
          title: document.title,
          description: document.description,
          admin_pct: document.fees.adminPct,
          admin_cap: document.fees.adminCap,
          contingency_pct: document.fees.contingencyPct,
          profit_pct: document.fees.profitPct,
          vat_enabled: document.vatEnabled,
          vat_pct: document.vatPct,
          input_vat_selected: document.inputVatSelected,
          deposit_amount: document.depositAmount,
          deposit_percentage: document.depositPercentage,
          has_deposit: document.hasDeposit,
          project_days: document.projectDays,
          project_hours: document.projectHours,
          issued_at: document.issuedAt,
          payment_structure_applicable: document.paymentStructureApplicable ?? false,
          payment_structure_pattern: document.paymentStructurePattern ?? null,
        })
        .select('*')
        .maybeSingle(); // Use maybeSingle() to handle edge cases
      if (vErr) throw new Error(vErr.message);
      if (!v1) {
        throw new Error('Failed to create Version 1: insert returned no data');
      }

      if (mapped.length > 0) {
        const verLines = mapped.map(l => ({
          version_id: v1.id,
          line_kind: l.lineKind,
          additional_type: l.additionalType ?? null,
          material_id: l.materialId || null,
          equipment_id: l.equipmentId || null,
          labour_rate_id: l.labourRateId || null,
          name: l.name,
          description: l.description || null,
          qty: l.qty,
          unit: l.unit || null,
          unit_price: l.unitPrice,
          vat_inclusive: l.vatInclusive,
          line_total: l.lineTotal,
          sort_order: l.sortOrder,
          labour_days: l.labourDays || null,
          labour_hours: l.labourHours || null,
          meta_json: (l as any).metaJson ?? null,
        }));
        const { error: vLinesErr } = await directSupabase.from('document_version_lines').insert(verLines);
        if (vLinesErr) throw new Error(vLinesErr.message);
      }

      // Point document to active version
      const { error: setActiveErr } = await directSupabase
        .from('documents')
        .update({ active_version_id: v1.id })
        .eq('id', document.id);
      if (setActiveErr) throw new Error(setActiveErr.message);
    }

    // Calculate and store totals in database
    await calculateAndStoreTotals(
      document.id,
      mapped,
      document.fees,
      document.vatEnabled,
      document.vatPct
    );

    const totals = computeTotals(
      mapped.map(l => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal, vatInclusive: l.vatInclusive })),
      document.fees,
      { enabled: document.vatEnabled, pct: document.vatPct }
    );
    if (document.kind === 'Quotation' && document.projectId) {
      await unlinkOtherQuotationsFromProject(document.projectId, document.id);
    }
    return { ...document, lines: mapped, totals } as DocumentWithLines;
  },

  async getDocument(id: string): Promise<DocumentWithLines | null> {
    const { data, error } = await directSupabase
      .from('documents')
      .select('*, document_lines(*)')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    const doc = fromDbDocument(data);
    const lines: DocumentLine[] = (data.document_lines || []).map(fromDbLine);
    const computed = getComputedSnapshot(lines, doc.fees, doc.vatEnabled, doc.vatPct);
    const totals = computed.totals;
    if (doc.kind === 'Invoice') {
      const storedNet = roundMoney(Number(doc.netTotal ?? 0));
      const storedVat = roundMoney(Number(doc.vatAmount ?? 0));
      const storedGross = roundMoney(Number(doc.grossTotal ?? 0));
      const deltaNet = Math.abs(storedNet - computed.netTotal);
      const deltaVat = Math.abs(storedVat - computed.vatAmount);
      const deltaGross = Math.abs(storedGross - computed.grossTotal);
      const hasStored = doc.netTotal != null || doc.vatAmount != null || doc.grossTotal != null;
      if (hasStored && (deltaNet > 0.01 || deltaVat > 0.01 || deltaGross > 0.01)) {
        console.warn('Invoice totals drift detected', {
          documentId: doc.id,
          lifecycleRef: doc.lifecycleRef,
          stored: { net: storedNet, vat: storedVat, gross: storedGross },
          computed: { net: computed.netTotal, vat: computed.vatAmount, gross: computed.grossTotal },
          delta: { net: deltaNet, vat: deltaVat, gross: deltaGross }
        });
        if (normalizeInvoiceRole(doc.invoiceRole)) {
          const { error: healErr } = await directSupabase
            .from('documents')
            .update({
              net_total: computed.netTotal,
              vat_amount: computed.vatAmount,
              gross_total: computed.grossTotal,
            })
            .eq('id', doc.id);
          if (!healErr) {
            doc.netTotal = computed.netTotal;
            doc.vatAmount = computed.vatAmount;
            doc.grossTotal = computed.grossTotal;
          }
        }
      }
    }
    return { ...doc, lines, totals };
  },

  async listDocuments(params: { organisationId?: string; source?: 'quotedock' | 'jarvis'; kind?: 'Estimate' | 'Quotation' | 'Invoice'; status?: string; search?: string } = {}): Promise<Document[]> {
    let query = directSupabase.from('documents').select('*').order('created_at', { ascending: false });
    if (params.organisationId) query = query.eq('organisation_id', params.organisationId);
    if (params.source) query = query.eq('source', params.source);
    if (params.kind) query = query.eq('kind', params.kind);
    if (params.status) query = query.eq('status', params.status);
    // Exclude void documents unless specifically requested
    if (!params.status) {
      query = query.neq('status', 'void');
    }
    // Simple search on ref/title
    if (params.search) {
      const term = params.search;
      query = query.or(`lifecycle_ref.ilike.%${term}%,invoice_ref.ilike.%${term}%,title.ilike.%${term}%`);
    }
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data || []).map(fromDbDocument);
  },

  async getDocumentsByProject(projectId: string): Promise<Document[]> {
    const { data, error } = await directSupabase
      .from('documents')
      .select('*')
      .eq('project_id', projectId)
      .neq('status', 'void')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(fromDbDocument);
  },

  async getDocumentsForCustomer(customerId: string): Promise<DocumentWithLines[]> {
    const { data, error } = await directSupabase
      .from('documents')
      .select('*, document_lines(*)')
      .eq('customer_id', customerId)
      .neq('status', 'void')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    const docs: DocumentWithLines[] = (data || []).map((row: any) => {
      const doc = fromDbDocument(row);
      const lines: DocumentLine[] = (row.document_lines || []).map(fromDbLine);
      const totals = computeTotals(
        lines.map(l => ({ lineKind: l.lineKind, qty: l.qty, unitPrice: l.unitPrice, lineTotal: l.lineTotal, vatInclusive: l.vatInclusive })),
        doc.fees,
        { enabled: doc.vatEnabled, pct: doc.vatPct }
      );
      return { ...doc, lines, totals } as DocumentWithLines;
    });
    return docs;
  },

  async getActiveVersionNumbers(documentIds: string[]): Promise<Record<string, number>> {
    const summaries = await this.getActiveVersionSummaries(documentIds);
    const map: Record<string, number> = {};
    for (const [documentId, summary] of Object.entries(summaries)) {
      map[documentId] = summary.versionNumber;
    }
    return map;
  },

  async getActiveVersionSummaries(documentIds: string[]): Promise<Record<string, {
    versionId: string;
    versionNumber: number;
    isTransmitted: boolean;
    overrideUnlocked: boolean;
    title: string | null;
  }>> {
    const ids = [...new Set(documentIds.map((id) => String(id || '').trim()).filter(Boolean))];
    if (ids.length === 0) return {};
    const [{ data: docs, error: docsError }, { data: versions, error: versionsError }] = await Promise.all([
      directSupabase
        .from('documents')
        .select('id, active_version_id')
        .in('id', ids),
      directSupabase
        .from('document_versions')
        .select('id, document_id, version_number, is_transmitted, override_unlocked, is_active, title')
        .in('document_id', ids),
    ]);
    if (docsError) throw new Error(docsError.message);
    if (versionsError) throw new Error(versionsError.message);
    const byDocumentId = new Map<string, any[]>();
    for (const row of versions || []) {
      const documentId = String((row as { document_id?: string }).document_id || '').trim();
      if (!documentId) continue;
      const list = byDocumentId.get(documentId) || [];
      list.push(row);
      byDocumentId.set(documentId, list);
    }
    const map: Record<string, {
      versionId: string;
      versionNumber: number;
      isTransmitted: boolean;
      overrideUnlocked: boolean;
      title: string | null;
    }> = {};
    for (const doc of docs || []) {
      const documentId = String((doc as { id?: string }).id || '').trim();
      const rows = byDocumentId.get(documentId) || [];
      const activeVersionId = String((doc as { active_version_id?: string }).active_version_id || '').trim();
      const versionRow =
        rows.find((row) => !!(row as { is_active?: boolean }).is_active) ||
        rows.find((row) => String((row as { id?: string }).id || '') === activeVersionId);
      const versionNumber = Number((versionRow as { version_number?: number } | undefined)?.version_number);
      const versionId = String((versionRow as { id?: string } | undefined)?.id || '').trim();
      if (!documentId || !versionId || !Number.isFinite(versionNumber) || versionNumber <= 0) continue;
      map[documentId] = {
        versionId,
        versionNumber: Math.trunc(versionNumber),
        isTransmitted: !!((versionRow as { is_transmitted?: boolean }).is_transmitted),
        overrideUnlocked: !!((versionRow as { override_unlocked?: boolean }).override_unlocked),
        title: String((versionRow as { title?: string } | undefined)?.title || '').trim() || null,
      };
    }
    return map;
  },

  async listQuoteLikeVersionsForProject(projectId: string): Promise<QuoteLikeVersion[]> {
    const docs = (await this.getDocumentsByProject(projectId))
      .filter((doc) => doc.kind === 'Estimate' || doc.kind === 'Quotation');
    if (docs.length === 0) return [];
    const ids = docs.map((doc) => doc.id);
    const metaById = new Map(docs.map((doc) => [doc.id, doc]));
    const { data, error } = await directSupabase
      .from('document_versions')
      .select('id, document_id, version_number, is_active, is_transmitted, override_unlocked, visual_survey_id')
      .in('document_id', ids)
      .order('version_number', { ascending: true });
    const fallback = error && /visual_survey_id/i.test(error.message)
      ? await directSupabase
        .from('document_versions')
        .select('id, document_id, version_number, is_active, is_transmitted, override_unlocked')
        .in('document_id', ids)
        .order('version_number', { ascending: true })
      : { data, error };
    if (fallback.error) throw new Error(fallback.error.message);
    return (fallback.data || []).flatMap((row: any) => {
      const documentId = String(row.document_id || '').trim();
      const doc = metaById.get(documentId);
      if (!doc || (doc.kind !== 'Estimate' && doc.kind !== 'Quotation')) return [];
      const versionNumber = Number(row.version_number);
      if (!Number.isFinite(versionNumber) || versionNumber <= 0) return [];
      return [{
        id: String(row.id),
        documentId,
        versionNumber: Math.trunc(versionNumber),
        isActive: !!row.is_active,
        isTransmitted: !!row.is_transmitted,
        overrideUnlocked: !!row.override_unlocked,
        visualSurveyId: row.visual_survey_id ? String(row.visual_survey_id) : null,
        lifecycleRef: String(doc.lifecycleRef || '').trim(),
        kind: doc.kind,
      }];
    });
  },

  async getAllDocumentsForLinking(): Promise<Document[]> {
    const { data, error } = await directSupabase
      .from('documents')
      .select('*')
              .in('kind', ['Estimate', 'Quotation', 'Invoice'])
      .neq('status', 'void')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(fromDbDocument);
  },

  async syncInvoicesForLifecycle(organisationId: string, lifecycleRef: string): Promise<void> {
    try {
      // Find the quotation for this lifecycle
      // Use limit(1) to ensure we only get one row, then maybeSingle() to handle zero rows
      const { data: quote, error: qErr } = await directSupabase
        .from('documents')
        .select('id')
        .eq('organisation_id', organisationId)
        .eq('lifecycle_ref', lifecycleRef)
        .eq('kind', 'Quotation')
        .limit(1)
        .maybeSingle();
      if (qErr) {
        console.error('Error fetching quotation for invoice sync:', {
          organisationId,
          lifecycleRef,
          error: qErr.message,
          errorCode: qErr.code,
        });
        throw new Error(qErr.message);
      }
      if (!quote?.id) {
        console.warn(`No quotation found for lifecycle ${lifecycleRef}, skipping invoice sync`);
        return;
      }

      // Load active version header for quote
      const active = await this.getActiveVersion(quote.id);
    if (!active?.header) {
      console.warn(`No active version found for quotation ${quote.id}, skipping sync`);
      return;
    }
    const h: any = active.header;

    // Fetch all invoices to update. Deposit, sent, paid, and frozen invoices stay put.
    const { data: invoices, error: invErr } = await directSupabase
      .from('documents')
      .select('id, is_original, invoice_role, frozen_at, status')
      .eq('organisation_id', organisationId)
      .eq('lifecycle_ref', lifecycleRef)
      .eq('kind', 'Invoice')
      .neq('status', 'void');
    if (invErr) throw new Error(invErr.message);
    if (!invoices || invoices.length === 0) return;

    const updatableInvoices = (invoices || []).filter((x: any) => invoiceCanBeLifecycleSynced({
      invoiceRole: x.invoice_role,
      frozenAt: x.frozen_at,
      status: x.status,
      isOriginal: x.is_original,
    }));
    const updatable = updatableInvoices.map((x: any) => x.id);
    if (updatable.length === 0) return;

    // Update header-level fields to mirror quote active version
    const { error: upErr } = await directSupabase
      .from('documents')
      .update({
        title: h.title,
        description: h.description,
        admin_pct: h.admin_pct,
        admin_cap: h.admin_cap,
        contingency_pct: h.contingency_pct,
        profit_pct: h.profit_pct,
        vat_enabled: h.vat_enabled,
        vat_pct: h.vat_pct,
        input_vat_selected: h.input_vat_selected,
        has_deposit: h.has_deposit,
        deposit_amount: h.deposit_amount,
        deposit_percentage: h.deposit_percentage,
        project_days: h.project_days,
        project_hours: h.project_hours,
        payment_structure_applicable: h.payment_structure_applicable,
        payment_structure_pattern: h.payment_structure_pattern,
      })
      .in('id', updatable);
    if (upErr) throw new Error(upErr.message);

    // Replace invoice base lines with the quote's active version lines to keep items in sync
    if ((active.lines || []).length > 0) {
      const linePayloads = (invoiceId: string) =>
        (active.lines || []).map((l: any, idx: number) => ({
          document_id: invoiceId,
          line_kind: l.line_kind,
          material_id: l.material_id || null,
          equipment_id: l.equipment_id || null,
          labour_rate_id: l.labour_rate_id || null,
          name: l.name,
          description: l.description || null,
          qty: Number(l.qty || 0),
          unit: l.unit || null,
          unit_price: Number(l.unit_price || 0),
          vat_inclusive: !!l.vat_inclusive,
          line_total: l.line_total != null ? Number(l.line_total) : null,
          sort_order: l.sort_order != null ? l.sort_order : idx,
          additional_type: l.additional_type ?? null,
          labour_days: l.labour_days || null,
          labour_hours: l.labour_hours || null,
          meta_json: l.meta_json ?? null,
        }));

      for (const inv of updatableInvoices) {
        if (inv.is_original) continue;
        await writeDocumentLines(inv.id, linePayloads(inv.id));
        
        // Recalculate and store totals for synced invoice
        const { data: syncedLines } = await directSupabase
          .from('document_lines')
          .select('*')
          .eq('document_id', inv.id);
        const mappedLines = (syncedLines || []).map(fromDbLine);
        await calculateAndStoreTotals(
          inv.id,
          mappedLines,
          {
            adminPct: Number(h.admin_pct ?? 0),
            adminCap: Number(h.admin_cap ?? 0),
            contingencyPct: Number(h.contingency_pct ?? 0),
            profitPct: Number(h.profit_pct ?? 0),
            developerPct: null,
          },
          !!h.vat_enabled,
          Number(h.vat_pct ?? 0.2)
        );
      }
    }
    } catch (error: any) {
      console.error('Error in syncInvoicesForLifecycle:', {
        organisationId,
        lifecycleRef,
        error: error?.message,
        stack: error?.stack,
      });
      // Re-throw with more context
      throw new Error(`Failed to sync invoices for lifecycle ${lifecycleRef}: ${error?.message || 'Unknown error'}`);
    }
  },

  async updateDocument(id: string, patch: Partial<DocumentInput>): Promise<Document> {
    // Get current document to check for status change
    const { data: currentDoc, error: currentDocError } = await directSupabase
      .from('documents')
      .select('status, kind, is_original, project_id, freeagent_invoice_url, invoice_role, frozen_at')
      .eq('id', id)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
    
    if (currentDocError) {
      throw new Error(`Failed to fetch document ${id}: ${currentDocError.message}`);
    }
    
    if (!currentDoc) {
      throw new Error(`Document ${id} not found`);
    }

    if (patch.projectId !== undefined) {
      assertCanAssignDocumentToProject(
        (currentDoc as { project_id?: string | null }).project_id,
        patch.projectId
      );
    }

    const oldStatus = String(currentDoc.status || '').trim().toLowerCase();
    const nextStatus = patch.status != null ? String(patch.status).trim().toLowerCase() : '';
    const isStatusChange = Boolean(nextStatus && nextStatus !== oldStatus);
    if (isStatusChange && patch.status) {
      // Normalize chip / registry values (Accepted → accepted, Cash → cash).
      patch = { ...patch, status: nextStatus as DocumentInput['status'] };
    }
    const projectId = (currentDoc as { project_id?: string | null }).project_id;
    if (
      isStatusChange
      && projectId
      && quoteStatusRevertBlocked({
        documentKind: currentDoc.kind,
        fromStatus: oldStatus,
        toStatus: nextStatus,
        finalWorkPhase: 'in_progress',
      })
    ) {
      const { data: finals, error: finalErr } = await directSupabase
        .from('visual_designer_surveys')
        .select('option_id, completion_status, work_started_at, reopened_at')
        .eq('project_id', projectId)
        .eq('kind', 'final');
      if (finalErr) throw new Error(finalErr.message);
      const rows = finals || [];
      const final = rows.find((row: any) => !row.option_id) || rows[0];
      const phase = finalSurveyWorkPhase(final ? {
        completionStatus: final.completion_status,
        workStartedAt: final.work_started_at,
        reopenedAt: final.reopened_at,
      } : null);
      if (quoteStatusRevertBlocked({ documentKind: currentDoc.kind, fromStatus: oldStatus, toStatus: nextStatus, finalWorkPhase: phase })) {
        throw new Error(QUOTE_STATUS_REVERT_BLOCKED_MESSAGE);
      }
    }

    const isInvoice = currentDoc.kind === 'Invoice';
    const isQuoteLinkedInvoice = isInvoice && !invoiceAmountEditable({
      invoiceRole: currentDoc.invoice_role,
      frozenAt: currentDoc.frozen_at,
      status: currentDoc.status,
      isOriginal: currentDoc.is_original,
      freeagentInvoiceUrl: currentDoc.freeagent_invoice_url,
    });
    const amountAffectingPatch =
      patch.fees !== undefined ||
      patch.vatEnabled !== undefined ||
      patch.vatPct !== undefined ||
      patch.depositAmount !== undefined ||
      patch.depositPercentage !== undefined ||
      patch.hasDeposit !== undefined ||
      patch.paymentStructureApplicable !== undefined ||
      patch.paymentStructurePattern !== undefined;
    if (isQuoteLinkedInvoice && amountAffectingPatch) {
      throw new Error('This invoice is linked to a quote. Update quote values first, then sync to invoice.');
    }
    if (
      isInvoice
      && invoicePaymentScheduleLocked({ freeagentInvoiceUrl: (currentDoc as { freeagent_invoice_url?: string | null }).freeagent_invoice_url })
      && (patch.issuedAt !== undefined || patch.paymentTermsInDays !== undefined)
    ) {
      throw new Error('Invoice date and payment terms are locked after this invoice was issued to FreeAgent.');
    }

    const payload: any = {};
    const userId = await currentUserId();
    if (userId) payload.updated_by = userId;
    if (patch.source) payload.source = patch.source;
    if (patch.kind) payload.kind = patch.kind;
    if (patch.lifecycleRef) payload.lifecycle_ref = patch.lifecycleRef;
    if (patch.organisationId) payload.organisation_id = patch.organisationId;
    if (patch.projectId !== undefined) payload.project_id = patch.projectId;
    if (patch.customerId !== undefined) payload.customer_id = patch.customerId;
    if (patch.quotedockCustomerId !== undefined) payload.quotedock_customer_id = patch.quotedockCustomerId;
    if (patch.customerFirstName !== undefined) payload.customer_first_name = patch.customerFirstName;
    if (patch.customerLastName !== undefined) payload.customer_last_name = patch.customerLastName;
    if (patch.customerTitle !== undefined) payload.customer_title = patch.customerTitle;
    if (patch.title !== undefined) payload.title = patch.title;
    if (patch.description !== undefined) payload.description = patch.description;
    if (patch.notes !== undefined) payload.notes = patch.notes;
    if (patch.status) {
      payload.status = patch.status;
      Object.assign(payload, getDocumentStatusTimestampPatch(patch.status, oldStatus));
      const freezeStatus = String(patch.status || '').trim().toLowerCase();
      if (isInvoice && (freezeStatus === 'sent' || freezeStatus === 'paid' || freezeStatus === 'accepted' || freezeStatus === 'cash')) {
        payload.frozen_at = new Date().toISOString();
      }
    }
    if (patch.currency) payload.currency = patch.currency;
    if (patch.depositAmount !== undefined) payload.deposit_amount = patch.depositAmount;
    if (patch.depositPercentage !== undefined) payload.deposit_percentage = patch.depositPercentage;
    if (patch.hasDeposit !== undefined) payload.has_deposit = patch.hasDeposit;
    if (patch.projectDays !== undefined) payload.project_days = patch.projectDays;
    if (patch.projectHours !== undefined) payload.project_hours = patch.projectHours;
    if (patch.paymentStructureApplicable !== undefined) payload.payment_structure_applicable = patch.paymentStructureApplicable;
    if (patch.paymentStructurePattern !== undefined) payload.payment_structure_pattern = patch.paymentStructurePattern;
    if (patch.vatEnabled !== undefined) payload.vat_enabled = patch.vatEnabled;
    if (patch.vatPct !== undefined) payload.vat_pct = patch.vatPct;
    if (patch.inputVatSelected !== undefined) payload.input_vat_selected = patch.inputVatSelected;
    if (patch.issuedAt !== undefined) payload.issued_at = patch.issuedAt;
    if (patch.paymentTermsInDays !== undefined) payload.payment_terms_in_days = patch.paymentTermsInDays;
    if (patch.fees) {
      payload.admin_pct = patch.fees.adminPct;
      payload.admin_cap = patch.fees.adminCap;
      payload.contingency_pct = patch.fees.contingencyPct;
      payload.profit_pct = patch.fees.profitPct;
    }

    let { data, error } = await directSupabase
      .from('documents')
      .update(payload)
      .eq('id', id)
      .select('*')
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist

    if (error && /payment_terms_in_days|schema cache/i.test(error.message)) {
      const fallback = { ...payload } as Record<string, unknown>;
      delete fallback.payment_terms_in_days;
      ({ data, error } = await directSupabase
        .from('documents')
        .update(fallback)
        .eq('id', id)
        .select('*')
        .maybeSingle());
    }
    
    if (error) {
      console.error('Error updating document:', {
        documentId: id,
        error: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        payload,
      });
      throw new Error(error.message);
    }
    
    if (!data) {
      console.error('Document update returned no data:', {
        documentId: id,
        payload,
      });
      throw new Error(`Document ${id} not found or could not be updated`);
    }
    
    const updatedDoc = fromDbDocument(data);
    if (updatedDoc.kind === 'Quotation' && patch.projectId) {
      await unlinkOtherQuotationsFromProject(patch.projectId, updatedDoc.id);
    }

    // Recalculate totals if fees, VAT settings, or lines changed
    const needsRecalc = patch.fees || patch.vatEnabled !== undefined || patch.vatPct !== undefined;
    if (needsRecalc) {
      // Fetch current lines to recalculate
      const { data: currentLines } = await directSupabase
        .from('document_lines')
        .select('*')
        .eq('document_id', id);
      const lines = (currentLines || []).map(fromDbLine);
      await calculateAndStoreTotals(
        id,
        lines,
        updatedDoc.fees,
        updatedDoc.vatEnabled,
        updatedDoc.vatPct
      );
    }

    if (
      isStatusChange
      && (nextStatus === 'accepted' || nextStatus === 'cash')
      && (currentDoc.kind === 'Quotation' || currentDoc.kind === 'Estimate')
      && currentDoc.project_id
    ) {
      if (currentDoc.kind === 'Estimate') {
        try {
          const quote = await this.progressAcceptedEstimateToQuote(id);
          if (quote?.id) {
            (updatedDoc as Document & { progressedQuoteId?: string }).progressedQuoteId = quote.id;
          }
        } catch (err) {
          console.error('Accepted estimate → quote promotion failed:', err);
          (updatedDoc as Document & { progressQuoteError?: string }).progressQuoteError =
            err instanceof Error ? err.message : 'Failed to create quote from accepted estimate';
        }
      }
      // Final lives on the quote only. After an estimate accept the quote already exists.
      try {
        const hasFinal = await visualDesignerSurveysService.projectHasFinalSurvey(String(currentDoc.project_id));
        if (!hasFinal) {
          await visualDesignerSurveysService.createFinalSurveyFromFirst(String(currentDoc.project_id));
        }
      } catch (err) {
        console.error('Legacy Final Survey ensure after accept/cash failed:', err);
        (updatedDoc as Document & { finalSurveyProvisionError?: string }).finalSurveyProvisionError =
          err instanceof Error ? err.message : 'Failed to ensure Final Survey';
      }
    }

    const projectIdForActivity = updatedDoc.projectId || currentDoc.project_id || null;
    if (isStatusChange && nextStatus && projectIdForActivity) {
      emitActivityEventBackground({
        action: 'document.status_changed',
        entityType: 'document',
        entityId: updatedDoc.id,
        projectId: String(projectIdForActivity),
        actorId: userId,
        label: `${docActivityNoun(updatedDoc.kind)} status set to ${nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}${updatedDoc.lifecycleRef ? ` (${updatedDoc.lifecycleRef})` : ''}`,
        metadata: {
          kind: updatedDoc.kind,
          from: oldStatus,
          to: nextStatus,
          lifecycleRef: updatedDoc.lifecycleRef,
        },
      });
    }
    if (
      patch.projectId !== undefined
      && String(patch.projectId || '') !== String(currentDoc.project_id || '')
      && patch.projectId
    ) {
      emitActivityEventBackground({
        action: 'document.linked',
        entityType: 'document',
        entityId: updatedDoc.id,
        projectId: String(patch.projectId),
        actorId: userId,
        label: `${docActivityNoun(updatedDoc.kind)} linked to workspace${updatedDoc.lifecycleRef ? ` (${updatedDoc.lifecycleRef})` : ''}`,
        metadata: { kind: updatedDoc.kind, lifecycleRef: updatedDoc.lifecycleRef },
      });
    }

    // Trigger FreeAgent sync if status changed to sent|paid|rejected
    // Only for invoices (FreeAgent sync is invoice-specific)
    if (isStatusChange && patch.status && ['sent', 'paid', 'rejected'].includes(patch.status) && currentDoc.kind === 'Invoice') {
      const newStatus = patch.status; // TypeScript narrowing

      console.log('🔄 Triggering FreeAgent sync for status change:', {
        documentId: id,
        oldStatus,
        newStatus,
        userId,
      });

      // Trigger FreeAgent sync asynchronously (don't block the update)
      import('../../../integrations/freeagentSync').then(({ handleInvoiceStatusChange }) => {
        handleInvoiceStatusChange(id, newStatus, oldStatus, userId || undefined).catch((err) => {
          console.error('❌ FreeAgent sync failed:', {
            documentId: id,
            error: err?.message,
            stack: err?.stack,
          });
          // Error is already logged in handleInvoiceStatusChange
        });
      }).catch((importError) => {
        console.error('❌ Failed to import FreeAgent sync module:', importError);
      });
    }

    return updatedDoc;
  },

  async deleteDocument(id: string): Promise<void> {
    const { data: existing } = await directSupabase
      .from('documents')
      .select('id, kind, lifecycle_ref, project_id')
      .eq('id', id)
      .maybeSingle();

    const { error: fullDeleteErr } = await directSupabase.rpc('delete_document_fully', { p_document_id: id });
    if (fullDeleteErr && !isMissingRpcError(String(fullDeleteErr.message || ''))) {
      throw new Error(fullDeleteErr.message);
    }

    // Invoices may be referenced by job_actuals (ON DELETE RESTRICT).
    if (fullDeleteErr && existing?.kind === 'Invoice') {
      const { error: actualsErr } = await directSupabase
        .from('job_actuals')
        .delete()
        .eq('invoice_id', id);
      if (actualsErr && !/does not exist|relation .* does not exist/i.test(String(actualsErr.message || ''))) {
        throw new Error(actualsErr.message);
      }
    }

    if (fullDeleteErr) {
      // Hard delete: remove from database
      // First delete associated lines
      const { error: lineError } = await directSupabase
        .from('document_lines')
        .delete()
        .eq('document_id', id);
      if (lineError) throw new Error(lineError.message);

      // Then delete the document
      const { error } = await directSupabase
        .from('documents')
        .delete()
        .eq('id', id);
      if (error) throw new Error(error.message);
    }

    if (existing?.project_id) {
      emitActivityEventBackground({
        action: 'document.deleted',
        entityType: 'document',
        entityId: String(existing.id),
        projectId: String(existing.project_id),
        label: `${docActivityNoun(existing.kind)} deleted${existing.lifecycle_ref ? ` (${existing.lifecycle_ref})` : ''}`,
        metadata: { kind: existing.kind, lifecycleRef: existing.lifecycle_ref },
      });
    }
  },

  async replaceLines(documentId: string, lines: DocumentLineInput[]): Promise<DocumentLine[]> {
    const { data: docInfo, error: docInfoErr } = await directSupabase
      .from('documents')
      .select('kind, is_original, invoice_role, frozen_at, status, freeagent_invoice_url')
      .eq('id', documentId)
      .maybeSingle();
    if (docInfoErr) throw new Error(docInfoErr.message);
    if (!docInfo) throw new Error(`Document ${documentId} not found when replacing lines`);
    if (docInfo.kind === 'Invoice' && !invoiceAmountEditable({
      invoiceRole: docInfo.invoice_role,
      frozenAt: docInfo.frozen_at,
      status: docInfo.status,
      isOriginal: docInfo.is_original,
      freeagentInvoiceUrl: docInfo.freeagent_invoice_url,
    })) {
      throw new Error('This invoice is linked to a quote. Update quote lines first, then sync to invoice.');
    }

    const saved = await writeDocumentLines(documentId, lines.map(l => toDbLine(documentId, l)));
    if (lines.length === 0) {
      // Update totals to zero when all lines are removed
      await directSupabase
        .from('documents')
        .update({ net_total: 0, vat_amount: 0, gross_total: 0 })
        .eq('id', documentId);
      return [];
    }
    const mappedLines = saved.map(fromDbLine);
    
    // Get document to recalculate totals
    const { data: doc, error: docErr } = await directSupabase
      .from('documents')
      .select('admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct')
      .eq('id', documentId)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
    if (docErr) throw new Error(docErr.message);
    if (!doc) {
      throw new Error(`Document ${documentId} not found when replacing lines`);
    }
    
    // Recalculate and store totals
    await calculateAndStoreTotals(
      documentId,
      mappedLines,
      {
        adminPct: Number(doc.admin_pct ?? 0),
        adminCap: Number(doc.admin_cap ?? 0),
        contingencyPct: Number(doc.contingency_pct ?? 0),
        profitPct: Number(doc.profit_pct ?? 0),
        developerPct: null,
      },
      !!doc.vat_enabled,
      Number(doc.vat_pct ?? 0.2)
    );
    
    return mappedLines;
  },

  async recalculateInvoiceTotalsBackfill(): Promise<{ checked: number; updated: number; failed: number }> {
    const { data: invoices, error } = await directSupabase
      .from('documents')
      .select('id, lifecycle_ref, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct, net_total, vat_amount, gross_total')
      .eq('kind', 'Invoice')
      .neq('status', 'void');
    if (error) throw new Error(error.message);

    let checked = 0;
    let updated = 0;
    let failed = 0;

    for (const invoice of invoices || []) {
      checked += 1;
      try {
        const { data: lineRows, error: lineErr } = await directSupabase
          .from('document_lines')
          .select('*')
          .eq('document_id', invoice.id);
        if (lineErr) throw new Error(lineErr.message);
        const lines = (lineRows || []).map(fromDbLine);

        const computed = getComputedSnapshot(
          lines,
          {
            adminPct: Number(invoice.admin_pct ?? 0),
            adminCap: Number(invoice.admin_cap ?? 0),
            contingencyPct: Number(invoice.contingency_pct ?? 0),
            profitPct: Number(invoice.profit_pct ?? 0),
            developerPct: null
          },
          !!invoice.vat_enabled,
          Number(invoice.vat_pct ?? 0.2)
        );

        const storedNet = roundMoney(Number(invoice.net_total ?? 0));
        const storedVat = roundMoney(Number(invoice.vat_amount ?? 0));
        const storedGross = roundMoney(Number(invoice.gross_total ?? 0));
        const hasDelta =
          Math.abs(storedNet - computed.netTotal) > 0.01 ||
          Math.abs(storedVat - computed.vatAmount) > 0.01 ||
          Math.abs(storedGross - computed.grossTotal) > 0.01;

        await calculateAndStoreTotals(
          invoice.id,
          lines,
          {
            adminPct: Number(invoice.admin_pct ?? 0),
            adminCap: Number(invoice.admin_cap ?? 0),
            contingencyPct: Number(invoice.contingency_pct ?? 0),
            profitPct: Number(invoice.profit_pct ?? 0),
            developerPct: null
          },
          !!invoice.vat_enabled,
          Number(invoice.vat_pct ?? 0.2)
        );

        if (hasDelta) updated += 1;
      } catch (err) {
        failed += 1;
        console.warn('Failed invoice totals backfill row', {
          invoiceId: invoice.id,
          lifecycleRef: invoice.lifecycle_ref,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }

    return { checked, updated, failed };
  },

  async listLifecycleInvoices(organisationId: string, lifecycleRef: string): Promise<Document[]> {
    const { data, error } = await directSupabase
      .from('documents')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('lifecycle_ref', lifecycleRef)
      .eq('kind', 'Invoice')
      .order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    return (data || []).map(fromDbDocument);
  },

  async createRoleInvoice(args: {
    source: Document;
    role: InvoiceRole;
    amount: number;
    title: string;
    lineName: string;
    sourceSurveyEvent?: string | null;
    depositAmount?: number;
    depositPercentage?: number | null;
    hasDeposit?: boolean;
    freeze?: boolean;
    quotedGross?: number;
    lineDescription?: string | null;
    sourceHeader?: { vat_enabled?: boolean | null; vat_pct?: number | null } | null;
  }): Promise<DocumentWithLines> {
    const amount = Math.round((Math.abs(Number(args.amount) || 0) + Number.EPSILON) * 100) / 100;
    if (amount <= 0 && args.role !== 'variation') {
      throw new Error('Invoice amount must be greater than zero');
    }
    let sourceHeader = args.sourceHeader;
    if (sourceHeader === undefined) {
      sourceHeader = await this.getActiveVersion(args.source.id)
        .then((loaded) => loaded?.header || null)
        .catch(() => null);
    }
    const { vatEnabled, vatPct } = resolveSourceVat(args.source, sourceHeader);
    const nowIso = new Date().toISOString();
    const depositNarrative = args.role === 'deposit'
      ? depositInvoiceNarrative({
        lifecycleRef: args.source.lifecycleRef,
        quotedGross: resolveQuotedGrossForDeposit({
          quotedGross: args.quotedGross ?? args.source.grossTotal,
          depositAmount: args.depositAmount ?? args.source.depositAmount,
          depositPercentage: args.depositPercentage ?? args.source.depositPercentage,
        }),
        sourceKind: args.source.kind,
      })
      : null;
    const input: DocumentInput = {
      kind: 'Invoice',
      lifecycleRef: args.source.lifecycleRef,
      organisationId: args.source.organisationId,
      isOriginal: false,
      projectId: args.source.projectId ?? null,
      customerId: args.source.customerId ?? null,
      quotedockCustomerId: args.source.quotedockCustomerId ?? null,
      customerFirstName: args.source.customerFirstName ?? null,
      customerLastName: args.source.customerLastName ?? null,
      customerTitle: args.source.customerTitle ?? null,
      title: args.title,
      description: depositNarrative ?? args.source.description ?? null,
      notes: args.role === 'deposit' ? depositNarrative : (args.source.notes ?? null),
      status: 'draft',
      currency: args.source.currency,
      depositAmount: args.depositAmount ?? 0,
      depositPercentage: args.depositPercentage ?? null,
      hasDeposit: args.hasDeposit ?? false,
      projectDays: args.source.projectDays ?? null,
      projectHours: args.source.projectHours ?? null,
      vatEnabled,
      vatPct,
      inputVatSelected: args.source.inputVatSelected,
      issuedAt: null,
      paymentTermsInDays: defaultPaymentTermsInDays(args.role),
      paymentStructureApplicable: false,
      paymentStructurePattern: null,
      invoiceRole: args.role,
      frozenAt: args.freeze ? nowIso : null,
      sourceSurveyEvent: args.sourceSurveyEvent ?? null,
      fees: {
        adminPct: 0,
        adminCap: 0,
        contingencyPct: 0,
        profitPct: 0,
        developerPct: null,
      },
      source: 'jarvis',
    };
    return this.createDocument(input, [{
      lineKind: 'other',
      additionalType: 'other',
      name: args.role === 'deposit'
        ? depositInvoiceLineName({
            quotedGross: resolveQuotedGrossForDeposit({
              quotedGross: args.quotedGross ?? args.source.grossTotal,
              depositAmount: args.depositAmount ?? args.source.depositAmount,
              depositPercentage: args.depositPercentage ?? args.source.depositPercentage,
            }),
            depositPercentage: args.depositPercentage ?? args.source.depositPercentage,
            fallback: args.lineName,
          })
        : args.lineName,
      description: args.lineDescription ?? depositNarrative ?? null,
      qty: 1,
      unit: 'item',
      unitPrice: amount,
      vatInclusive: vatEnabled,
      sortOrder: 0,
    }]);
  },

  async ensureDepositInvoice(
    fromDocumentId: string,
    sourceVersionId?: string | null,
  ): Promise<DocumentWithLines | null> {
    const source = await this.getDocument(fromDocumentId);
    if (!source) throw new Error('Source document not found');

    const existing = await this.listLifecycleInvoices(source.organisationId, source.lifecycleRef);
    const live = existing.filter((invoice) => String(invoice.status || '').toLowerCase() !== 'void');
    const existingDeposit = live.find((invoice) => normalizeInvoiceRole(invoice.invoiceRole) === 'deposit');
    if (existingDeposit) {
      return this.getDocument(existingDeposit.id);
    }
    // Legacy full-job clones were backfilled as frozen works. Do not add a second deposit on top.
    if (live.length > 0) {
      return this.getDocument(live[0].id);
    }

    let headerOverride: any | null = null;
    const resolvedVersionId = String(sourceVersionId || '').trim() || null;
    try {
      const loaded = resolvedVersionId
        ? await this.getVersion(resolvedVersionId)
        : await this.getActiveVersion(fromDocumentId);
      headerOverride = loaded?.header || null;
    } catch {
      headerOverride = null;
    }

    const hasDeposit = headerOverride?.has_deposit != null ? !!headerOverride.has_deposit : source.hasDeposit;
    const depositAmount = headerOverride?.deposit_amount != null
      ? Number(headerOverride.deposit_amount)
      : Number(source.depositAmount || 0);
    const depositPercentage = headerOverride?.deposit_percentage != null
      ? Number(headerOverride.deposit_percentage)
      : source.depositPercentage;
    if (!hasDeposit || !(depositAmount > 0)) {
      return null;
    }

    const sourceEvent = resolvedVersionId
      || String(source.activeVersionId || '').trim()
      || `first-accept:${source.id}`;
    return this.createRoleInvoice({
      source,
      role: 'deposit',
      amount: depositAmount,
      title: `Deposit — ${headerOverride?.title || source.title}`,
      lineName: 'Deposit',
      sourceSurveyEvent: sourceEvent,
      depositAmount,
      depositPercentage,
      hasDeposit: true,
      freeze: true,
      quotedGross: Number(source.grossTotal || 0),
      sourceHeader: headerOverride,
    });
  },

  async issueBalanceInvoice(
    fromDocumentId: string,
    options?: {
      settledContract?: number;
      sourceSurveyEvent?: string | null;
      amount?: number;
      role?: InvoiceRole;
      title?: string;
      lineName?: string;
    },
  ): Promise<DocumentWithLines | null> {
    const source = await this.getDocument(fromDocumentId);
    if (!source) throw new Error('Source document not found');

    const existing = await this.listLifecycleInvoices(source.organisationId, source.lifecycleRef);
    const settledContract = options?.settledContract != null
      ? Number(options.settledContract)
      : Number(source.grossTotal || 0);
    const scheduledAmount = options?.amount != null ? Number(options.amount) : null;
    const ledger = computeInvoiceDelta({
      settledContract,
      invoices: existing.map((invoice) => ({
        id: invoice.id,
        role: invoice.invoiceRole,
        status: invoice.status,
        grossTotal: invoice.grossTotal,
        depositAmount: invoice.depositAmount,
      })),
    });
    const role = options?.role || ledger.nextRole;
    const amount = scheduledAmount != null ? scheduledAmount : ledger.delta;
    if (!role || !(Math.abs(amount) > 0)) {
      return null;
    }

    const sourceEvent = String(options?.sourceSurveyEvent || '').trim() || `settled:${ledger.settledContract}`;
    const matchingDraft = existing.find((invoice) => (
      normalizeInvoiceRole(invoice.invoiceRole) === role
      && String(invoice.status || '').toLowerCase() === 'draft'
      && String(invoice.sourceSurveyEvent || '') === sourceEvent
    ));
    if (matchingDraft) {
      return this.getDocument(matchingDraft.id);
    }

    const roleLabel = invoiceRoleLabel(role);
    return this.createRoleInvoice({
      source,
      role,
      amount: Math.abs(amount),
      title: options?.title || `${roleLabel} — ${source.title}`,
      lineName: options?.lineName
        || (role === 'credit' ? 'Credit' : (role === 'variation' ? 'Variation' : 'Balance due')),
      sourceSurveyEvent: sourceEvent,
      depositAmount: 0,
      depositPercentage: null,
      hasDeposit: false,
      // A scheduled stage bills its own amount; the quote sync must never rewrite it to the full quote.
      freeze: scheduledAmount != null,
    });
  },

  /** Deduction lines on each of these invoices, keyed by invoice id. */
  async listBalanceDeductions(invoiceIds: string[]): Promise<Record<string, Array<{ name: string; amount: number }>>> {
    const ids = invoiceIds.filter(Boolean);
    if (!ids.length) return {};
    const { data, error } = await directSupabase
      .from('document_lines')
      .select('document_id, name, qty, unit_price, line_total, meta_json, sort_order')
      .in('document_id', ids)
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    const byInvoice: Record<string, Array<{ name: string; amount: number }>> = {};
    (data || []).forEach((row: any) => {
      if (!isDeductionLine(row)) return;
      const list = byInvoice[String(row.document_id)] || [];
      list.push({ name: String(row.name || 'Deduction'), amount: deductionAmount(row) });
      byInvoice[String(row.document_id)] = list;
    });
    return byInvoice;
  },

  /**
   * Replaces the deduction lines on a reissued balance that is still a draft. Only deductions
   * can change: the balance line itself stays as issued. The quote sync never touches the
   * invoice (it is frozen), so this writes the lines directly.
   */
  async setBalanceDeductions(
    invoiceId: string,
    deductions: Array<{ name: string; amount: number }>,
  ): Promise<DocumentWithLines> {
    const invoice = await this.getDocument(invoiceId);
    if (!invoice) throw new Error('Invoice not found');
    if (!reissuedFromInvoiceId(invoice.sourceSurveyEvent)) {
      throw new Error('Deductions can only be added to a reissued balance.');
    }
    const { data: syncRow, error: syncErr } = await directSupabase
      .from('documents')
      .select('freeagent_invoice_url')
      .eq('id', invoiceId)
      .maybeSingle();
    if (syncErr) throw new Error(syncErr.message);
    if (String(invoice.status || '').toLowerCase() !== 'draft' || (syncRow as any)?.freeagent_invoice_url) {
      throw new Error('Deductions are fixed once the reissued balance has been sent.');
    }
    const clean = deductions
      .map((item) => ({ name: String(item.name || '').trim() || 'Agreed reduction', amount: roundMoney(Math.abs(Number(item.amount) || 0)) }))
      .filter((item) => item.amount > 0);
    const kept = (invoice.lines || []).filter((line) => !isDeductionLine(line));
    const keptGross = kept.reduce((sum, line) => sum + (line.lineTotal != null ? Number(line.lineTotal) : Number(line.qty || 0) * Number(line.unitPrice || 0)), 0);
    const deducted = clean.reduce((sum, item) => sum + item.amount, 0);
    if (roundMoney(keptGross - deducted) <= 0) {
      throw new Error('Deductions cannot take the balance to zero or below.');
    }
    // Net amounts that land the invoice exactly on balance − deductions; entered amounts stay in meta
    const nets = deductionLineNets({
      keptLines: kept,
      amounts: clean.map((item) => item.amount),
      vatEnabled: !!invoice.vatEnabled,
      vatPct: Number(invoice.vatPct ?? 0.2),
    });
    const nextOrder = kept.reduce((max, line) => Math.max(max, Number(line.sortOrder || 0)), 0) + 1;
    const lines: DocumentLineInput[] = [
      ...kept.map((line) => ({ ...line })),
      ...clean.map((item, index) => ({
        lineKind: 'other' as const,
        additionalType: 'other' as const,
        name: item.name,
        description: null,
        qty: 1,
        unit: 'item',
        unitPrice: nets[index],
        vatInclusive: false,
        sortOrder: nextOrder + index,
        metaJson: { deduction: true, amount: item.amount },
      })),
    ];
    const saved = (await writeDocumentLines(invoiceId, lines.map((line) => toDbLine(invoiceId, line)))).map(fromDbLine);
    await calculateAndStoreTotals(
      invoiceId,
      saved,
      { adminPct: 0, adminCap: 0, contingencyPct: 0, profitPct: 0, developerPct: null },
      !!invoice.vatEnabled,
      Number(invoice.vatPct ?? 0.2),
    );
    const updated = await this.getDocument(invoiceId);
    if (!updated) throw new Error('Invoice not found after saving deductions');
    return updated;
  },

  async cloneToInvoice(fromDocumentId: string, sourceVersionId?: string | null): Promise<DocumentWithLines> {
    const created = await this.ensureDepositInvoice(fromDocumentId, sourceVersionId);
    if (created) return created;
    const source = await this.getDocument(fromDocumentId);
    if (!source) throw new Error('Source document not found');
    const existing = await this.listLifecycleInvoices(source.organisationId, source.lifecycleRef);
    const live = existing.find((invoice) => String(invoice.status || '').toLowerCase() !== 'void');
    if (live) {
      const existingDoc = await this.getDocument(live.id);
      if (existingDoc) return existingDoc;
    }
    throw new Error('No deposit is configured on the accepted survey, so no deposit invoice was created.');
  },

  async cloneToQuotation(fromDocumentId: string, options?: {
    status?: Document['status'];
    sourceVersionId?: string | null;
    skipSurveyClone?: boolean;
  }): Promise<DocumentWithLines> {
    const source = await this.getDocument(fromDocumentId);
    if (!source) throw new Error('Source document not found');

    // Create a new quotation based on the source (estimate)
    // Prefer an explicit version (accepted First snapshot), then active, then base document.
    let headerOverride: any | null = null;
    let versionLines: DocumentLineInput[] | null = null;
    try {
      const preferredId = String(options?.sourceVersionId || '').trim();
      const loaded = preferredId
        ? await this.getVersion(preferredId).catch(() => null)
        : await this.getActiveVersion(fromDocumentId);
      if (loaded) {
        headerOverride = loaded.header;
        versionLines = (loaded.lines || []).map((l: any) => ({
          lineKind: l.line_kind,
          additionalType: l.additional_type ?? null,
          materialId: l.material_id || null,
          equipmentId: l.equipment_id || null,
          labourRateId: l.labour_rate_id || null,
          name: l.name,
          description: l.description || null,
          qty: Number(l.qty || 0),
          unit: l.unit || null,
          unitPrice: Number(l.unit_price || 0),
          vatInclusive: !!l.vat_inclusive,
          lineTotal: l.line_total != null ? Number(l.line_total) : undefined,
          sortOrder: l.sort_order ?? null,
          labourDays: l.labour_days ?? null,
          labourHours: l.labour_hours ?? null,
          metaJson: l.meta_json ?? null,
        }));
      }
    } catch (_) {
      // ignore and use fallback
    }

    const input: DocumentInput = {
      kind: 'Quotation',
      lifecycleRef: source.lifecycleRef, // keep lifecycle
      organisationId: source.organisationId,
      projectId: source.projectId ?? null,
      customerId: source.customerId ?? null,
      quotedockCustomerId: source.quotedockCustomerId ?? null,
      customerFirstName: source.customerFirstName ?? null,
      customerLastName: source.customerLastName ?? null,
      customerTitle: source.customerTitle ?? null,
      title: headerOverride?.title ?? source.title,
      description: headerOverride?.description ?? source.description ?? null,
      notes: source.notes ?? null,
      status: options?.status || 'draft',
      currency: source.currency,
      depositAmount: headerOverride?.deposit_amount != null ? Number(headerOverride.deposit_amount) : source.depositAmount,
      depositPercentage: headerOverride?.deposit_percentage != null ? Number(headerOverride.deposit_percentage) : source.depositPercentage,
      hasDeposit: headerOverride?.has_deposit != null ? !!headerOverride.has_deposit : source.hasDeposit,
      projectDays: headerOverride?.project_days ?? source.projectDays ?? null,
      projectHours: headerOverride?.project_hours ?? source.projectHours ?? null,
      vatEnabled: headerOverride?.vat_enabled != null ? !!headerOverride.vat_enabled : source.vatEnabled,
      vatPct: headerOverride?.vat_pct != null ? Number(headerOverride.vat_pct) : source.vatPct,
      inputVatSelected: headerOverride?.input_vat_selected != null ? !!headerOverride.input_vat_selected : source.inputVatSelected,
      issuedAt: headerOverride?.issued_at ?? null,
      paymentStructureApplicable:
        headerOverride?.payment_structure_applicable != null
          ? !!headerOverride.payment_structure_applicable
          : !!source.paymentStructureApplicable,
      paymentStructurePattern:
        headerOverride?.payment_structure_pattern != null
          ? String(headerOverride.payment_structure_pattern)
          : source.paymentStructurePattern ?? null,
      fees: {
        adminPct: headerOverride?.admin_pct != null ? Number(headerOverride.admin_pct) : source.fees.adminPct,
        adminCap: headerOverride?.admin_cap != null ? Number(headerOverride.admin_cap) : source.fees.adminCap,
        contingencyPct: headerOverride?.contingency_pct != null ? Number(headerOverride.contingency_pct) : source.fees.contingencyPct,
        profitPct: headerOverride?.profit_pct != null ? Number(headerOverride.profit_pct) : source.fees.profitPct,
        developerPct: null,
      },
      source: 'jarvis',
    };

    const created = await this.createDocument(input, (versionLines ?? source.lines) as unknown as DocumentLineInput[]);
    if (!options?.skipSurveyClone) {
      try {
        const sourceActive = await this.getActiveVersion(fromDocumentId);
        const createdActive = await this.getActiveVersion(created.id);
        const sourceVersionId = sourceActive?.header?.id;
        const createdVersionId = createdActive?.header?.id;
        if (sourceVersionId && createdVersionId && created.projectId) {
          const surveys = await visualDesignerSurveysService.listByProject(created.projectId);
          const sourceSurvey = surveys.find((s) => s.documentVersionId === String(sourceVersionId));
          if (sourceSurvey) {
            await visualDesignerSurveysService.cloneSurveyForVersion(
              sourceSurvey.id,
              String(createdVersionId),
              'Quote survey',
              'intermediate'
            );
          }
        }
      } catch (err) {
        console.warn('Quote cloned; Visual Designer survey pairing skipped:', err);
      }
    }
    return created;
  },

  /**
   * After an Estimate First Survey is Accepted/Cash, promote the workspace onto a draft quote.
   * Quote First is a historical clone of Estimate First. Final is created on the quote.
   * The quote stays Draft until Final is sent and the quote lifecycle continues as normal.
   * Idempotent if a quote already exists on the project.
   */
  async progressAcceptedEstimateToQuote(estimateId: string): Promise<DocumentWithLines | null> {
    const estimate = await this.getDocument(estimateId);
    if (!estimate || estimate.kind !== 'Estimate') return null;
    const status = String(estimate.status || '').trim().toLowerCase();
    if (status !== 'accepted' && status !== 'cash') return null;
    const projectId = String(estimate.projectId || '').trim();
    if (!projectId) return null;

    const { data: existingQuotes, error: quoteErr } = await directSupabase
      .from('documents')
      .select('id')
      .eq('project_id', projectId)
      .eq('kind', 'Quotation')
      .order('created_at', { ascending: false })
      .limit(1);
    if (quoteErr) throw new Error(quoteErr.message);
    if (existingQuotes?.[0]?.id) {
      return this.getDocument(String(existingQuotes[0].id));
    }

    const surveys = await visualDesignerSurveysService.listByProject(projectId);
    const first = surveys.find((row) => row.kind === 'first' && !row.optionId)
      || surveys.find((row) => row.kind === 'first')
      || null;
    const final = surveys.find((row) => row.kind === 'final' && !row.optionId)
      || surveys.find((row) => row.kind === 'final')
      || null;
    const firstVersionId = String(first?.documentVersionId || '').trim()
      || String((await this.getActiveVersion(estimate.id).catch(() => null))?.header?.id || '').trim()
      || null;

    const created = await this.cloneToQuotation(estimate.id, {
      status: 'draft',
      sourceVersionId: firstVersionId,
      skipSurveyClone: true,
    });

    const quoteFirstId = String((await this.getActiveVersion(created.id).catch(() => null))?.header?.id || '').trim();
    if (first && quoteFirstId) {
      await visualDesignerSurveysService.rebindToVersion(first.id, quoteFirstId);
      try {
        await this.markVersionTransmitted(quoteFirstId);
      } catch (err) {
        console.warn('Could not lock cloned Quote First as transmitted:', err);
      }
    }

    if (final?.documentVersionId) {
      try {
        const finalOnQuote = await this.cloneVersionFrom(created.id, String(final.documentVersionId), {
          includeVisualDesigner: true,
          allowCrossDocument: true,
        });
        await directSupabase
          .from('document_versions')
          .update({
            is_transmitted: false,
            override_unlocked: false,
            transmitted_at: null,
            transmitted_by: null,
          })
          .eq('id', finalOnQuote.id);
        await visualDesignerSurveysService.rebindToVersion(final.id, finalOnQuote.id);
        await this.activateVersion(created.id, finalOnQuote.id);
      } catch (err) {
        console.warn('Final Survey rebind onto accepted quote failed:', err);
      }
    } else {
      try {
        await visualDesignerSurveysService.createFinalSurveyFromFirst(projectId, {
          fromTransmissionVersionId: quoteFirstId || firstVersionId,
        });
      } catch (err) {
        console.warn('Final Survey provision after estimate accept failed:', err);
      }
    }

    return this.getDocument(created.id);
  },

  // VERSIONING SERVICES
  async listVersions(documentId: string): Promise<Array<{
    id: string;
    versionNumber: number;
    isActive: boolean;
    isTransmitted: boolean;
    transmittedAt: string | null;
    overrideUnlocked: boolean;
    overrideUnlockedAt: string | null;
    visualSurveyId: string | null;
    createdAt: string;
    updatedAt: string;
  }>> {
    const { data, error } = await directSupabase
      .from('document_versions')
      .select('id, version_number, is_active, is_transmitted, transmitted_at, override_unlocked, override_unlocked_at, created_at, updated_at, visual_survey_id')
      .eq('document_id', documentId)
      .order('version_number', { ascending: true });
    const fallback = error && /visual_survey_id/i.test(error.message)
      ? await directSupabase
        .from('document_versions')
        .select('id, version_number, is_active, is_transmitted, transmitted_at, override_unlocked, override_unlocked_at, created_at, updated_at')
        .eq('document_id', documentId)
        .order('version_number', { ascending: true })
      : { data, error };
    if (fallback.error) throw new Error(fallback.error.message);
    return (fallback.data || []).map((v: any) => ({
      id: v.id,
      versionNumber: v.version_number,
      isActive: !!v.is_active,
      isTransmitted: !!v.is_transmitted,
      transmittedAt: v.transmitted_at ? String(v.transmitted_at) : null,
      overrideUnlocked: !!v.override_unlocked,
      overrideUnlockedAt: v.override_unlocked_at ? String(v.override_unlocked_at) : null,
      visualSurveyId: v.visual_survey_id ? String(v.visual_survey_id) : null,
      createdAt: v.created_at,
      updatedAt: v.updated_at,
    }));
  },

  async getActiveVersion(documentId: string): Promise<{ header: any; lines: any[] } | null> {
    const { data: doc, error: docErr } = await directSupabase
      .from('documents')
      .select('active_version_id, kind')
      .eq('id', documentId)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
    if (docErr) {
      console.error('Error fetching document for active version:', {
        documentId,
        error: docErr.message,
        errorCode: docErr.code,
      });
      throw new Error(docErr.message);
    }
    if (!doc) {
      console.warn(`Document ${documentId} not found when getting active version`);
      return null;
    }
    
    // For invoices, return null since they don't use versions
    if (doc.kind === 'Invoice') {
      return null;
    }
    
    const { data: flaggedRows, error: flaggedErr } = await directSupabase
      .from('document_versions')
      .select('*')
      .eq('document_id', documentId)
      .eq('is_active', true)
      .order('version_number', { ascending: false })
      .limit(1);
    if (flaggedErr) {
      console.error('Error fetching flagged active version:', {
        documentId,
        error: flaggedErr.message,
        errorCode: flaggedErr.code,
      });
      throw new Error(flaggedErr.message);
    }
    const flaggedActive = Array.isArray(flaggedRows) ? flaggedRows[0] : flaggedRows;

    const activeVersionId = String(flaggedActive?.id || doc.active_version_id || '').trim();

    // Keep documents.active_version_id aligned with the version marked is_active.
    if (flaggedActive?.id && String(doc.active_version_id || '') !== String(flaggedActive.id)) {
      await directSupabase
        .from('documents')
        .update({ active_version_id: flaggedActive.id })
        .eq('id', documentId);
    }

    // If no active version exists, create Version 1 from the base document
    if (!activeVersionId) {
      return await this.createVersion1FromBaseDocument(documentId);
    }

    let header = flaggedActive;
    if (!header) {
      const { data: byIdHeader, error: hErr } = await directSupabase
        .from('document_versions')
        .select('*')
        .eq('id', activeVersionId)
        .maybeSingle();
      if (hErr) throw new Error(hErr.message);
      header = byIdHeader;
    }
    if (!header) {
      console.warn(`Active version ${activeVersionId} not found for document ${documentId}, creating Version 1`);
      return await this.createVersion1FromBaseDocument(documentId);
    }

    const { data: lines, error: lErr } = await directSupabase
      .from('document_version_lines')
      .select('*')
      .eq('version_id', header.id)
      .order('sort_order', { ascending: true });
    if (lErr) throw new Error(lErr.message);

    return { header, lines: lines || [] };
  },

  async createVersion1FromBaseDocument(documentId: string): Promise<{ header: any; lines: any[] } | null> {
    try {
      // Get the base document
      const { data: baseDoc, error: baseErr } = await directSupabase
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
      if (baseErr) {
        console.error('Error fetching base document for Version 1 creation:', {
          documentId,
          error: baseErr.message,
          errorCode: baseErr.code,
        });
        throw new Error(baseErr.message);
      }
      if (!baseDoc) {
        throw new Error(`Document ${documentId} not found`);
      }
      if (baseDoc.kind === 'Invoice') {
        // Invoices are versionless
        return null;
      }

      // Get the base document lines
      const { data: baseLines, error: linesErr } = await directSupabase
        .from('document_lines')
        .select('*')
        .eq('document_id', documentId)
        .order('sort_order', { ascending: true });
      if (linesErr) throw new Error(linesErr.message);

      // Create Version 1 header
      const { data: versionHeader, error: headerErr } = await directSupabase
        .from('document_versions')
        .insert({
          document_id: documentId,
          version_number: 1,
          is_active: true,
          title: baseDoc.title,
          description: baseDoc.description,
          admin_pct: baseDoc.fees?.adminPct || 0,
          admin_cap: baseDoc.fees?.adminCap || 0,
          contingency_pct: baseDoc.fees?.contingencyPct || 0,
          profit_pct: baseDoc.fees?.profitPct || 0,
          developer_pct: baseDoc.fees?.developerPct || null,
          vat_enabled: baseDoc.vatEnabled || false,
          vat_pct: baseDoc.vatPct || 0.2,
          input_vat_selected: baseDoc.inputVatSelected || false,
          deposit_amount: baseDoc.depositAmount || 0,
          deposit_percentage: baseDoc.depositPercentage || null,
          has_deposit: baseDoc.hasDeposit || false,
          project_days: baseDoc.projectDays || null,
          project_hours: baseDoc.projectHours || null,
        })
        .select()
        .maybeSingle(); // Use maybeSingle() to handle edge cases
      if (headerErr) throw new Error(headerErr.message);
      if (!versionHeader) {
        throw new Error('Failed to create Version 1: insert returned no data');
      }

      // Create Version 1 lines
      if (baseLines && baseLines.length > 0) {
        const versionLines = baseLines.map((line: any, index: number) => ({
          version_id: versionHeader.id,
          line_kind: line.lineKind,
          additional_type: line.additional_type ?? null,
          material_id: line.materialId,
          equipment_id: line.equipmentId,
          labour_rate_id: line.labourRateId,
          name: line.name,
          description: line.description,
          qty: line.qty,
          unit: line.unit,
          unit_price: line.unitPrice,
          vat_inclusive: line.vatInclusive || false,
          line_total: line.lineTotal,
          sort_order: line.sortOrder || index,
          labour_days: line.labourDays,
          labour_hours: line.labourHours,
          meta_json: line.meta_json ?? null,
        }));

        const { error: versionLinesErr } = await directSupabase
          .from('document_version_lines')
          .insert(versionLines);
        if (versionLinesErr) throw new Error(versionLinesErr.message);
      }

      // Update the document to point to this active version
      const { error: updateErr } = await directSupabase
        .from('documents')
        .update({ active_version_id: versionHeader.id })
        .eq('id', documentId);
      if (updateErr) throw new Error(updateErr.message);

      // Map base lines to DocumentLine format and recalculate totals
      const mappedLines: DocumentLine[] = (baseLines || []).map((line: any) => ({
        id: line.id,
        documentId: documentId,
        lineKind: line.line_kind,
        additionalType: line.additional_type ?? null,
        materialId: line.material_id,
        equipmentId: line.equipment_id,
        labourRateId: line.labour_rate_id,
        name: line.name,
        description: line.description,
        qty: Number(line.qty ?? 0),
        unit: line.unit,
        unitPrice: Number(line.unit_price ?? 0),
        lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
        vatInclusive: !!line.vat_inclusive,
        sortOrder: line.sort_order,
        labourDays: line.labour_days,
        labourHours: line.labour_hours,
        metaJson: line.meta_json ?? null,
        createdAt: line.created_at,
        updatedAt: line.updated_at,
      }));

      // Recalculate and store totals from Version 1
      await calculateAndStoreTotals(
        documentId,
        mappedLines,
        {
          adminPct: Number(versionHeader.admin_pct ?? 0),
          adminCap: Number(versionHeader.admin_cap ?? 0),
          contingencyPct: Number(versionHeader.contingency_pct ?? 0),
          profitPct: Number(versionHeader.profit_pct ?? 0),
          developerPct: null,
        },
        !!versionHeader.vat_enabled,
        Number(versionHeader.vat_pct ?? 0.2)
      );

      // Return the created version
      return {
        header: versionHeader,
        lines: baseLines || []
      };
    } catch (error) {
      console.error('Failed to create Version 1 from base document:', error);
      return null;
    }
  },

  async getVersion(versionId: string): Promise<{ header: any; lines: any[] }> {
    const { data: header, error: hErr } = await directSupabase
      .from('document_versions')
      .select('*')
      .eq('id', versionId)
      .maybeSingle(); // Use maybeSingle() to handle case where version doesn't exist
    if (hErr) {
      console.error('Error fetching version:', {
        versionId,
        error: hErr.message,
        errorCode: hErr.code,
      });
      throw new Error(hErr.message);
    }
    if (!header) {
      throw new Error(`Version ${versionId} not found`);
    }
    const { data: lines, error: lErr } = await directSupabase
      .from('document_version_lines')
      .select('*')
      .eq('version_id', versionId)
      .order('sort_order', { ascending: true });
    if (lErr) throw new Error(lErr.message);
    return { header, lines: lines || [] };
  },

  async createVersionFromActive(documentId: string): Promise<{ id: string; versionNumber: number }> {
    // Guard: invoices are versionless
    const { data: baseDocInfo, error: baseDocErr } = await directSupabase
      .from('documents')
      .select('kind')
      .eq('id', documentId)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
    if (baseDocErr) throw new Error(baseDocErr.message);
    if (!baseDocInfo) {
      throw new Error(`Document ${documentId} not found`);
    }
    if (baseDocInfo?.kind === 'Invoice') {
      throw new Error('Invoices are versionless');
    }
    // Fetch active version or construct from current document + lines if not set
    const { data: dv, error: dvErr } = await directSupabase
      .from('document_versions')
      .select('*')
      .eq('document_id', documentId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle(); // Use maybeSingle() to handle case where no versions exist yet
    if (dvErr) {
      console.error('Error fetching latest version:', {
        documentId,
        error: dvErr.message,
        errorCode: dvErr.code,
      });
      throw new Error(dvErr.message);
    }
    const nextNumber = (dv && dv.version_number ? Number(dv.version_number) : 0) + 1;

    // Prefer cloning from active version header if exists, else snapshot from documents
    const active = await this.getActiveVersion(documentId);
    let headerPayload: any;
    if (active) {
      const h = active.header;
      headerPayload = {
        document_id: documentId,
        version_number: nextNumber,
        is_active: false,
        title: h.title,
        description: h.description,
        admin_pct: h.admin_pct,
        admin_cap: h.admin_cap,
        contingency_pct: h.contingency_pct,
        profit_pct: h.profit_pct,
        vat_enabled: h.vat_enabled,
        vat_pct: h.vat_pct,
        input_vat_selected: h.input_vat_selected,
        deposit_amount: h.deposit_amount,
        deposit_percentage: h.deposit_percentage,
        has_deposit: h.has_deposit,
        project_days: h.project_days,
        project_hours: h.project_hours,
        issued_at: h.issued_at,
      };
    } else {
      const { data: base, error: bErr } = await directSupabase.from('documents').select('*').eq('id', documentId).maybeSingle();
      if (bErr) throw new Error(bErr.message);
      if (!base) {
        throw new Error(`Document ${documentId} not found`);
      }
      headerPayload = {
        document_id: documentId,
        version_number: nextNumber,
        is_active: false,
        title: base.title,
        description: base.description,
        admin_pct: base.admin_pct,
        admin_cap: base.admin_cap,
        contingency_pct: base.contingency_pct,
        profit_pct: base.profit_pct,
        vat_enabled: base.vat_enabled,
        vat_pct: base.vat_pct,
        input_vat_selected: base.input_vat_selected,
        deposit_amount: base.deposit_amount,
        deposit_percentage: base.deposit_percentage,
        has_deposit: base.has_deposit,
        project_days: base.project_days,
        project_hours: base.project_hours,
        issued_at: base.issued_at,
      };
    }

    const { data: inserted, error: iErr } = await directSupabase
      .from('document_versions')
      .insert(headerPayload)
      .select('*')
      .maybeSingle(); // Use maybeSingle() to handle edge cases
    if (iErr) throw new Error(iErr.message);
    if (!inserted) {
      throw new Error('Failed to create version: insert returned no data');
    }

    // Clone lines from active version lines or from document_lines
    let sourceLines: any[] = [];
    if (active) {
      sourceLines = active.lines;
    } else {
      const { data: baseLines, error: blErr } = await directSupabase
        .from('document_lines')
        .select('*')
        .eq('document_id', documentId)
        .order('sort_order', { ascending: true });
      if (blErr) throw new Error(blErr.message);
      sourceLines = baseLines || [];
    }

    if (sourceLines.length > 0) {
      const withoutVisualDesigner = sourceLines.filter((l: any) => String(l?.meta_json?.feature || l?.metaJson?.feature || '').trim() !== 'visual_designer');
      const payload = withoutVisualDesigner.map((l: any) => ({
        version_id: inserted.id,
        line_kind: l.line_kind,
        additional_type: l.additional_type ?? null,
        material_id: l.material_id,
        equipment_id: l.equipment_id,
        labour_rate_id: l.labour_rate_id,
        name: l.name,
        description: l.description,
        qty: l.qty,
        unit: l.unit,
        unit_price: l.unit_price,
        vat_inclusive: l.vat_inclusive,
        line_total: l.line_total,
        sort_order: l.sort_order,
        labour_days: l.labour_days,
        labour_hours: l.labour_hours,
        meta_json: l.meta_json ?? null,
      }));
      if (payload.length > 0) {
        const { error: insLinesErr } = await directSupabase.from('document_version_lines').insert(payload);
        if (insLinesErr) throw new Error(insLinesErr.message);
      }
    }

    return { id: inserted.id, versionNumber: inserted.version_number };
  },

  /**
   * Clone a specific version into a new inactive version.
   * - includeVisualDesigner=false: commercial lines only (Final Survey provision projects VD from survey items).
   * - includeVisualDesigner=true: full snapshot including Visual Designer lines (immutable transmissions / unlock forks).
   */
  async cloneVersionFrom(
    documentId: string,
    sourceVersionId: string,
    options?: { includeVisualDesigner?: boolean; allowCrossDocument?: boolean },
  ): Promise<{ id: string; versionNumber: number }> {
    const includeVisualDesigner = !!options?.includeVisualDesigner;
    const { data: baseDocInfo, error: baseDocErr } = await directSupabase
      .from('documents')
      .select('kind')
      .eq('id', documentId)
      .maybeSingle();
    if (baseDocErr) throw new Error(baseDocErr.message);
    if (!baseDocInfo) throw new Error(`Document ${documentId} not found`);
    if (baseDocInfo.kind === 'Invoice') throw new Error('Invoices are versionless');

    const loaded = await this.getVersion(sourceVersionId);
    if (!loaded?.header) throw new Error('Source version not found');
    if (!options?.allowCrossDocument && String(loaded.header.document_id) !== String(documentId)) {
      throw new Error('Source version does not belong to this document');
    }

    const { data: latest, error: latestErr } = await directSupabase
      .from('document_versions')
      .select('version_number')
      .eq('document_id', documentId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (latestErr) throw new Error(latestErr.message);
    const nextNumber = (latest?.version_number ? Number(latest.version_number) : 0) + 1;
    const h = loaded.header;

    const baseInsert: Record<string, unknown> = {
      document_id: documentId,
      version_number: nextNumber,
      is_active: false,
      title: h.title,
      description: h.description,
      admin_pct: h.admin_pct,
      admin_cap: h.admin_cap,
      contingency_pct: h.contingency_pct,
      profit_pct: h.profit_pct,
      vat_enabled: h.vat_enabled,
      vat_pct: h.vat_pct,
      input_vat_selected: h.input_vat_selected,
      deposit_amount: h.deposit_amount,
      deposit_percentage: h.deposit_percentage,
      has_deposit: h.has_deposit,
      project_days: h.project_days,
      project_hours: h.project_hours,
      issued_at: h.issued_at,
      payment_structure_applicable: h.payment_structure_applicable ?? false,
      payment_structure_pattern: h.payment_structure_pattern ?? null,
      // Never inherit transmission/override flags or survey bind onto clones.
      is_transmitted: false,
      override_unlocked: false,
      visual_survey_id: null,
      cloned_from_version_id: sourceVersionId,
      diverged_from_clone: false,
    };

    let inserted: { id: string; version_number: number } | null = null;
    {
      const { data, error: iErr } = await directSupabase
        .from('document_versions')
        .insert(baseInsert)
        .select('id, version_number')
        .maybeSingle();
      if (iErr && /cloned_from_version_id|diverged_from_clone|schema cache/i.test(String(iErr.message || ''))) {
        delete baseInsert.cloned_from_version_id;
        delete baseInsert.diverged_from_clone;
        const retry = await directSupabase
          .from('document_versions')
          .insert(baseInsert)
          .select('id, version_number')
          .maybeSingle();
        if (retry.error) throw new Error(retry.error.message);
        inserted = retry.data as any;
      } else if (iErr) {
        throw new Error(iErr.message);
      } else {
        inserted = data as any;
      }
    }
    if (!inserted || !inserted.id) throw new Error('Failed to clone version');
    const clonedHeader = inserted;

    const linesToCopy = includeVisualDesigner
      ? (loaded.lines || [])
      : (loaded.lines || []).filter(
        (l: any) => String(l?.meta_json?.feature || '').trim() !== 'visual_designer',
      );
    if (linesToCopy.length > 0) {
      const payload = linesToCopy.map((l: any) => ({
        version_id: clonedHeader.id,
        line_kind: l.line_kind,
        additional_type: l.additional_type ?? null,
        material_id: l.material_id,
        equipment_id: l.equipment_id,
        labour_rate_id: l.labour_rate_id,
        name: l.name,
        description: l.description,
        qty: l.qty,
        unit: l.unit,
        unit_price: l.unit_price,
        vat_inclusive: l.vat_inclusive,
        line_total: l.line_total,
        sort_order: l.sort_order,
        labour_days: l.labour_days,
        labour_hours: l.labour_hours,
        meta_json: l.meta_json ?? null,
      }));
      const { error: insLinesErr } = await directSupabase.from('document_version_lines').insert(payload);
      if (insLinesErr) throw new Error(insLinesErr.message);
    }

    return { id: String(clonedHeader.id), versionNumber: Number(clonedHeader.version_number) };
  },

  /**
   * Create an immutable customer-facing transmission snapshot from a working version.
   * The snapshot is marked transmitted and must never be edited; share links pin to it.
   * The working version is left unchanged (caller may lock it separately).
   */
  async createImmutableTransmission(
    workingVersionId: string,
  ): Promise<{ id: string; versionNumber: number; documentId: string }> {
    const { data: source, error: sourceErr } = await directSupabase
      .from('document_versions')
      .select('id, document_id')
      .eq('id', workingVersionId)
      .maybeSingle();
    if (sourceErr) throw new Error(sourceErr.message);
    if (!source?.id || !source.document_id) throw new Error('Working version not found');

    const cloned = await this.cloneVersionFrom(String(source.document_id), String(source.id), {
      includeVisualDesigner: true,
    });
    await this.markVersionTransmitted(cloned.id);
    return {
      id: cloned.id,
      versionNumber: cloned.versionNumber,
      documentId: String(source.document_id),
    };
  },

  async saveVersion(
    versionId: string,
    headerPatch: any,
    lines: any[],
    options?: { keepDesigns?: boolean },
  ): Promise<void> {
    // Look up owning document for this version
    const { data: owningVersion, error: ownErr } = await directSupabase
      .from('document_versions')
      .select('id, document_id, is_active, is_transmitted, override_unlocked')
      .eq('id', versionId)
      .maybeSingle(); // Use maybeSingle() to handle case where version doesn't exist
    if (ownErr) throw new Error(ownErr.message);
    if (!owningVersion) {
      throw new Error(`Version ${versionId} not found`);
    }
    // Immutable transmissions must never be mutated. Legacy in-place override_unlocked
    // still allows edits on already-unlocked rows; new unlocks fork a working copy instead.
    if (owningVersion.is_transmitted && !owningVersion.override_unlocked) {
      throw new Error('This transmission is locked. Unlock override to create a new working copy.');
    }

    // First Survey quote lines are historical once Final exists for the workspace.
    {
      const { data: versionMeta } = await directSupabase
        .from('document_versions')
        .select('visual_survey_id, document_id')
        .eq('id', versionId)
        .maybeSingle();
      const surveyId = String((versionMeta as any)?.visual_survey_id || '').trim();
      const documentIdForSurvey = String((versionMeta as any)?.document_id || owningVersion.document_id || '').trim();
      if (surveyId || documentIdForSurvey) {
        const { data: docRow } = await directSupabase
          .from('documents')
          .select('project_id')
          .eq('id', documentIdForSurvey)
          .maybeSingle();
        const projectId = String((docRow as any)?.project_id || '').trim();
        if (projectId) {
          const { visualDesignerSurveysService } = await import('./visualDesignerSurveys.service');
          const surveys = await visualDesignerSurveysService.listByProject(projectId);
          if (surveys.some((s) => s.kind === 'final')) {
            const bound = surveyId
              ? surveys.find((s) => s.id === surveyId)
              : surveys.find((s) => String(s.documentVersionId || '') === String(versionId));
            if (bound?.kind === 'first') {
              throw new Error(
                'First Survey is historical. Final Survey already exists — edit Final Survey instead.',
              );
            }
            if (bound?.kind === 'final' && bound.completionStatus === 'complete') {
              throw new Error(
                'Final Survey is complete. Reopen Final Survey before editing.',
              );
            }
            if (bound?.kind === 'final') {
              const currentDelta = await visualDesignerSurveysService.getCurrentFinalSurveyDelta(bound.id);
              if (currentDelta && (currentDelta.classification === 'pending_review' || currentDelta.classification == null) && !currentDelta.decidedAt && !currentDelta.supersededAt) {
                throw new Error(
                  'Final Survey has an unresolved review. Reopen Final Survey before editing.',
                );
              }
            }
          }
        }
      }
    }

    const { count: pinnedShareCount, error: pinErr } = await directSupabase
      .from('document_share_links')
      .select('id', { count: 'exact', head: true })
      .eq('sent_version_id', versionId);
    if (pinErr) throw new Error(pinErr.message);
    // Customer-pinned snapshots are immutable unless a legacy in-place override is already open.
    if ((pinnedShareCount || 0) > 0 && !owningVersion.override_unlocked) {
      throw new Error('This transmission is pinned to a customer link and cannot be edited. Unlock override to create a new working copy.');
    }

    // Save header
    const headerUpdate: any = {};
    const map: Record<string, string> = {
      title: 'title',
      description: 'description',
      adminPct: 'admin_pct',
      adminCap: 'admin_cap',
      contingencyPct: 'contingency_pct',
      profitPct: 'profit_pct',
      vatEnabled: 'vat_enabled',
      vatPct: 'vat_pct',
      inputVatSelected: 'input_vat_selected',
      depositAmount: 'deposit_amount',
      depositPercentage: 'deposit_percentage',
      hasDeposit: 'has_deposit',
      projectDays: 'project_days',
      projectHours: 'project_hours',
      issuedAt: 'issued_at',
      paymentStructureApplicable: 'payment_structure_applicable',
      paymentStructurePattern: 'payment_structure_pattern',
    };
    Object.entries(headerPatch || {}).forEach(([k, v]) => {
      if (map[k]) headerUpdate[map[k]] = v;
    });
    if (Object.keys(headerUpdate).length > 0) {
      const { error: uErr } = await directSupabase
        .from('document_versions')
        .update({ ...headerUpdate, diverged_from_clone: true })
        .eq('id', versionId);
      if (uErr && /diverged_from_clone|schema cache/i.test(String(uErr.message || ''))) {
        const retry = await directSupabase
          .from('document_versions')
          .update(headerUpdate)
          .eq('id', versionId);
        if (retry.error) throw new Error(retry.error.message);
      } else if (uErr) {
        throw new Error(uErr.message);
      }
    } else {
      await this.markVersionDivergedFromClone(versionId);
    }

    const payload = (lines || []).map((l: any, idx: number) => ({
      version_id: versionId,
      line_kind: l.lineKind,
      additional_type: l.additionalType ?? null,
      material_id: l.materialId || null,
      equipment_id: l.equipmentId || null,
      labour_rate_id: l.labourRateId || null,
      name: l.name,
      description: l.description || null,
      qty: l.qty || 0,
      unit: l.unit || null,
      unit_price: l.unitPrice || 0,
      vat_inclusive: !!l.vatInclusive,
      line_total: l.lineTotal != null ? l.lineTotal : (l.qty || 0) * (l.unitPrice || 0),
      sort_order: l.sortOrder != null ? l.sortOrder : idx,
      labour_days: l.labourDays || null,
      labour_hours: l.labourHours || null,
      meta_json: l.metaJson ?? null,
    }));
    await writeVersionLines(versionId, payload, !!options?.keepDesigns);

    // After save: if this version is active, recalculate and store document totals
    if (owningVersion.document_id && owningVersion.is_active) {
      // Get the updated version header and lines
      const { data: versionHeader, error: hErr } = await directSupabase
        .from('document_versions')
        .select('*')
        .eq('id', versionId)
        .maybeSingle(); // Use maybeSingle() to handle case where version doesn't exist
      if (hErr) throw new Error(hErr.message);
      if (!versionHeader) {
        throw new Error(`Version ${versionId} not found after save`);
      }

      const { data: versionLines, error: lErr } = await directSupabase
        .from('document_version_lines')
        .select('*')
        .eq('version_id', versionId)
        .order('sort_order', { ascending: true });
      if (lErr) throw new Error(lErr.message);

      // Map version lines to DocumentLine format
      const mappedLines: DocumentLine[] = (versionLines || []).map((line: any) => ({
        id: line.id,
        documentId: owningVersion.document_id,
        lineKind: line.line_kind,
        materialId: line.material_id,
        equipmentId: line.equipment_id,
        labourRateId: line.labour_rate_id,
        name: line.name,
        description: line.description,
        qty: Number(line.qty ?? 0),
        unit: line.unit,
        unitPrice: Number(line.unit_price ?? 0),
        lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
        vatInclusive: !!line.vat_inclusive,
        additionalType: line.additional_type ?? null,
        sortOrder: line.sort_order,
        labourDays: line.labour_days,
        labourHours: line.labour_hours,
        createdAt: line.created_at,
        updatedAt: line.updated_at,
      }));

      await syncDocumentPricingFromVersion(owningVersion.document_id, versionHeader);

      // Recalculate and store totals from active version
      await calculateAndStoreTotals(
        owningVersion.document_id,
        mappedLines,
        {
          adminPct: Number(versionHeader.admin_pct ?? 0),
          adminCap: Number(versionHeader.admin_cap ?? 0),
          contingencyPct: Number(versionHeader.contingency_pct ?? 0),
          profitPct: Number(versionHeader.profit_pct ?? 0),
          developerPct: null,
        },
        !!versionHeader.vat_enabled,
        Number(versionHeader.vat_pct ?? 0.2)
      );

      const title = String(versionHeader.title || '').trim();
      const { error: headerSyncErr } = await directSupabase
        .from('documents')
        .update({
          ...(title ? { title } : {}),
          description: versionHeader.description ?? null,
        })
        .eq('id', owningVersion.document_id);
      if (headerSyncErr) throw new Error(headerSyncErr.message);

      // If this version belongs to a Quotation and is active, sync linked invoices
      const { data: parentDoc, error: pErr } = await directSupabase
        .from('documents')
        .select('id, organisation_id, lifecycle_ref, kind')
        .eq('id', owningVersion.document_id)
        .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
      if (pErr) throw new Error(pErr.message);
      if (!parentDoc) {
        console.warn(`Parent document ${owningVersion.document_id} not found, skipping invoice sync`);
        return;
      }
      if (parentDoc?.kind === 'Quotation') {
        await documentsService.syncInvoicesForLifecycle(parentDoc.organisation_id, parentDoc.lifecycle_ref);
      }
    }

    // If a transmitted version was explicitly override-unlocked and edited,
    // keep pinned public-link totals in sync for that same version URL.
    if (owningVersion.is_transmitted) {
      const { data: versionHeader, error: vhErr } = await directSupabase
        .from('document_versions')
        .select('admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct')
        .eq('id', versionId)
        .maybeSingle();
      if (vhErr) throw new Error(vhErr.message);
      if (versionHeader) {
        const { data: versionLines, error: vlErr } = await directSupabase
          .from('document_version_lines')
          .select('*')
          .eq('version_id', versionId)
          .order('sort_order', { ascending: true });
        if (vlErr) throw new Error(vlErr.message);
        const mappedLines: DocumentLine[] = (versionLines || []).map((line: any) => ({
          id: line.id,
          documentId: owningVersion.document_id || '',
          lineKind: line.line_kind,
          materialId: line.material_id,
          equipmentId: line.equipment_id,
          labourRateId: line.labour_rate_id,
          name: line.name,
          description: line.description,
          qty: Number(line.qty ?? 0),
          unit: line.unit,
          unitPrice: Number(line.unit_price ?? 0),
          lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
          vatInclusive: !!line.vat_inclusive,
          additionalType: line.additional_type ?? null,
          sortOrder: line.sort_order,
          labourDays: line.labour_days,
          labourHours: line.labour_hours,
          createdAt: line.created_at,
          updatedAt: line.updated_at,
        }));
        await refreshShareLinkSnapshotsForVersion(
          versionId,
          mappedLines,
          {
            adminPct: Number(versionHeader.admin_pct ?? 0),
            adminCap: Number(versionHeader.admin_cap ?? 0),
            contingencyPct: Number(versionHeader.contingency_pct ?? 0),
            profitPct: Number(versionHeader.profit_pct ?? 0),
            developerPct: null,
          },
          !!versionHeader.vat_enabled,
          Number(versionHeader.vat_pct ?? 0.2)
        );
      }
    }
  },

  async markVersionTransmitted(versionId: string): Promise<void> {
    const nowIso = new Date().toISOString();
    const { data: userRes } = await directSupabase.auth.getUser();
    const userId = userRes.user?.id ?? null;
    const { data: row, error: rowErr } = await directSupabase
      .from('document_versions')
      .select('id, is_transmitted')
      .eq('id', versionId)
      .maybeSingle();
    if (rowErr) throw new Error(rowErr.message);
    if (!row?.id) throw new Error('Version not found');
    if (row.is_transmitted) return;
    const { error: upErr } = await directSupabase
      .from('document_versions')
      .update({
        is_transmitted: true,
        transmitted_at: nowIso,
        transmitted_by: userId,
        override_unlocked: false,
      })
      .eq('id', versionId);
    if (upErr) throw new Error(upErr.message);
  },

  async markVersionDivergedFromClone(versionId: string): Promise<void> {
    const id = String(versionId || '').trim();
    if (!id) return;
    const { error } = await directSupabase
      .from('document_versions')
      .update({ diverged_from_clone: true })
      .eq('id', id)
      .eq('diverged_from_clone', false);
    if (error && /diverged_from_clone|schema cache/i.test(String(error.message || ''))) {
      return;
    }
    if (error) throw new Error(error.message);
  },

  /**
   * Unlock for edits after a transmission/lock.
   * Always forks a new working copy so any customer-pinned transmission stays immutable.
   * Returns the editable working version id (differs from the input when forked).
   */
  async unlockVersionOverride(
    versionId: string,
    reason?: string,
  ): Promise<{ workingVersionId: string; forked: boolean }> {
    const nowIso = new Date().toISOString();
    const { data: userRes } = await directSupabase.auth.getUser();
    const userId = userRes.user?.id ?? null;
    const { data: row, error: rowErr } = await directSupabase
      .from('document_versions')
      .select('id, document_id, is_transmitted, visual_survey_id')
      .eq('id', versionId)
      .maybeSingle();
    if (rowErr) throw new Error(rowErr.message);
    if (!row?.id) throw new Error('Version not found');
    if (!row.is_transmitted) {
      throw new Error('Only locked/transmitted records can be override-unlocked');
    }

    // First Survey is permanently immutable after first transmission — never unlock.
    const documentId = String(row.document_id);
    const { data: docRow, error: docErr } = await directSupabase
      .from('documents')
      .select('id, project_id')
      .eq('id', documentId)
      .maybeSingle();
    if (docErr) throw new Error(docErr.message);
    const projectId = String(docRow?.project_id || '').trim();
    if (projectId) {
      const { visualDesignerSurveysService } = await import('./visualDesignerSurveys.service');
      const surveys = await visualDesignerSurveysService.listByProject(projectId);
      const surveyId = row.visual_survey_id ? String(row.visual_survey_id) : '';
      const bound = surveyId
        ? surveys.find((s) => s.id === surveyId)
        : surveys.find((s) => String(s.documentVersionId || '') === String(versionId));
      const first = surveys.find((s) => s.kind === 'first');
      const isFirstVersion = bound?.kind === 'first'
        || (!!first && String(first.documentVersionId || '') === String(versionId));
      if (isFirstVersion) {
        throw new Error(
          'First Survey is permanently immutable after transmission. Reopen/edit Final Survey instead.',
        );
      }
      // Completed Final must use Reopen Final Survey, not generic Unlock.
      if (bound?.kind === 'final' && bound.completionStatus === 'complete') {
        throw new Error(
          'Final Survey is complete. Use Reopen Final Survey to start another working cycle.',
        );
      }
    }

    const forked = await this.cloneVersionFrom(documentId, versionId, { includeVisualDesigner: true });
    const unlockReason = String(reason || '').trim() || null;

    // Keep the source as a locked historical/working lock row.
    await directSupabase
      .from('document_versions')
      .update({
        override_unlocked: false,
        override_relocked_at: nowIso,
        override_relocked_by: userId,
      })
      .eq('id', versionId);

    // Audit unlock intent on the new working copy (not transmitted — editable).
    await directSupabase
      .from('document_versions')
      .update({
        override_unlocked: false,
        override_unlocked_at: nowIso,
        override_unlocked_by: userId,
        override_unlock_reason: unlockReason,
      })
      .eq('id', forked.id);

    // Rebind survey from the locked row to the new working copy when applicable.
    const surveyId = row.visual_survey_id ? String(row.visual_survey_id) : '';
    if (surveyId) {
      await directSupabase
        .from('document_versions')
        .update({ visual_survey_id: null })
        .eq('id', versionId)
        .eq('visual_survey_id', surveyId);
      await directSupabase
        .from('visual_designer_surveys')
        .update({ document_version_id: forked.id })
        .eq('id', surveyId);
      await directSupabase
        .from('document_versions')
        .update({ visual_survey_id: surveyId })
        .eq('id', forked.id);
    } else {
      await directSupabase
        .from('visual_designer_surveys')
        .update({ document_version_id: forked.id })
        .eq('document_version_id', versionId);
    }

    await this.activateVersion(documentId, forked.id);
    return { workingVersionId: forked.id, forked: true };
  },

  async relockVersionOverride(versionId: string): Promise<void> {
    const nowIso = new Date().toISOString();
    const { data: userRes } = await directSupabase.auth.getUser();
    const userId = userRes.user?.id ?? null;
    const { data: row, error: rowErr } = await directSupabase
      .from('document_versions')
      .select('id, is_transmitted')
      .eq('id', versionId)
      .maybeSingle();
    if (rowErr) throw new Error(rowErr.message);
    if (!row?.id) throw new Error('Version not found');
    // After a fork unlock, the working copy is not transmitted — locking means mark transmitted
    // (staff lock) without creating a customer share link.
    if (!row.is_transmitted) {
      await this.markVersionTransmitted(versionId);
      return;
    }
    const { error: upErr } = await directSupabase
      .from('document_versions')
      .update({
        override_unlocked: false,
        override_relocked_at: nowIso,
        override_relocked_by: userId,
      })
      .eq('id', versionId);
    if (upErr) throw new Error(upErr.message);
  },

  async activateVersion(documentId: string, versionId: string): Promise<void> {
    // Keep activation resilient: some environments may miss/break the RPC that syncs active flags.
    const { error } = await directSupabase.rpc('set_active_document_version', { p_document_id: documentId, p_version_id: versionId });
    if (error) {
      const msg = String(error.message || '').toLowerCase();
      const canFallback =
        msg.includes('set_active_document_version') ||
        msg.includes('does not exist') ||
        msg.includes('undefined function');
      if (!canFallback) {
        throw new Error(error.message);
      }
    }

    // Ensure database state is definitely consistent for downstream flows
    // (send/public-doc reads documents.active_version_id).
    const { error: clearErr } = await directSupabase
      .from('document_versions')
      .update({ is_active: false })
      .eq('document_id', documentId);
    if (clearErr) throw new Error(clearErr.message);

    const { data: activatedRows, error: markErr } = await directSupabase
      .from('document_versions')
      .update({ is_active: true })
      .eq('id', versionId)
      .eq('document_id', documentId)
      .select('id');
    if (markErr) throw new Error(markErr.message);
    if (!Array.isArray(activatedRows) || activatedRows.length === 0) {
      throw new Error('Version not found for document');
    }

    const { error: docActiveErr } = await directSupabase
      .from('documents')
      .update({ active_version_id: versionId })
      .eq('id', documentId);
    if (docActiveErr) throw new Error(docActiveErr.message);

    // Get the activated version header and lines to recalculate totals
    const { data: versionHeader, error: hErr } = await directSupabase
      .from('document_versions')
      .select('*')
      .eq('id', versionId)
      .maybeSingle(); // Use maybeSingle() to handle case where version doesn't exist
    if (hErr) throw new Error(hErr.message);
    if (!versionHeader) {
      throw new Error(`Version ${versionId} not found`);
    }

    const { data: versionLines, error: lErr } = await directSupabase
      .from('document_version_lines')
      .select('*')
      .eq('version_id', versionId)
      .order('sort_order', { ascending: true });
    if (lErr) throw new Error(lErr.message);

    // Map version lines to DocumentLine format
    const mappedLines: DocumentLine[] = (versionLines || []).map((line: any) => ({
      id: line.id,
      documentId: documentId,
      lineKind: line.line_kind,
      additionalType: line.additional_type ?? null,
      materialId: line.material_id,
      equipmentId: line.equipment_id,
      labourRateId: line.labour_rate_id,
      name: line.name,
      description: line.description,
      qty: Number(line.qty ?? 0),
      unit: line.unit,
      unitPrice: Number(line.unit_price ?? 0),
      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
      vatInclusive: !!line.vat_inclusive,
      sortOrder: line.sort_order,
      labourDays: line.labour_days,
      labourHours: line.labour_hours,
      createdAt: line.created_at,
      updatedAt: line.updated_at,
    }));

    await syncDocumentPricingFromVersion(documentId, versionHeader);

    // Recalculate and store totals from activated version
    await calculateAndStoreTotals(
      documentId,
      mappedLines,
      {
        adminPct: Number(versionHeader.admin_pct ?? 0),
        adminCap: Number(versionHeader.admin_cap ?? 0),
        contingencyPct: Number(versionHeader.contingency_pct ?? 0),
        profitPct: Number(versionHeader.profit_pct ?? 0),
        developerPct: null,
      },
      !!versionHeader.vat_enabled,
      Number(versionHeader.vat_pct ?? 0.2)
    );

    const activatedTitle = String(versionHeader.title || '').trim();
    const { error: headerSyncErr } = await directSupabase
      .from('documents')
      .update({
        ...(activatedTitle ? { title: activatedTitle } : {}),
        description: versionHeader.description ?? null,
      })
      .eq('id', documentId);
    if (headerSyncErr) throw new Error(headerSyncErr.message);

    // Sync invoices for quotations after activation switch
    const { data: doc, error: dErr } = await directSupabase
      .from('documents')
      .select('organisation_id, lifecycle_ref, kind')
      .eq('id', documentId)
      .maybeSingle(); // Use maybeSingle() to handle case where document doesn't exist
    if (dErr) throw new Error(dErr.message);
    if (!doc) {
      console.warn(`Document ${documentId} not found after version activation, skipping invoice sync`);
      return;
    }
    if (doc?.kind === 'Quotation') {
      await documentsService.syncInvoicesForLifecycle(doc.organisation_id, doc.lifecycle_ref);
    }
  },

  async deleteVersion(versionId: string): Promise<void> {
    // Load version header first
    const { data: header, error: hErr } = await directSupabase
      .from('document_versions')
      .select('id, document_id, is_active')
      .eq('id', versionId)
      .maybeSingle(); // Use maybeSingle() to handle case where version doesn't exist
    if (hErr) throw new Error(hErr.message);
    if (!header) throw new Error('Version not found');
    if (header.is_active) throw new Error('Cannot delete the active version. Set another version active first.');

    // Ensure no document is pointing to this as active_version_id (FK typically prevents it, but we pre-check for better UX)
    const { data: pointingDoc, error: pErr } = await directSupabase
      .from('documents')
      .select('id')
      .eq('id', header.document_id)
      .eq('active_version_id', versionId)
      .maybeSingle();
    if (pErr) throw new Error(pErr.message);
    if (pointingDoc) throw new Error('Document references this version as active. Activate a different version, then delete.');

    // Delete lines then header
    const { error: dlErr } = await directSupabase.from('document_version_lines').delete().eq('version_id', versionId);
    if (dlErr) throw new Error(dlErr.message);
    const { error: dvErr } = await directSupabase.from('document_versions').delete().eq('id', versionId);
    if (dvErr) throw new Error(dvErr.message);
  },

  async copyVisualDesignerLinesToDocument(
    targetDocumentId: string,
    sourceLines: DocumentLineInput[],
    targetVersionId?: string,
    sourceVersionId?: string | null
  ): Promise<number> {
    const visualLines = (sourceLines || []).filter((line: any) => String(line?.metaJson?.feature || '').trim() === 'visual_designer');
    if (visualLines.length === 0) return 0;

    let resolvedTargetDocumentId = String(targetDocumentId || '').trim();
    let resolvedTargetVersionId = String(targetVersionId || '').trim();
    let targetDoc: any = null;

    if (resolvedTargetVersionId) {
      const { data: versionRow, error: versionErr } = await directSupabase
        .from('document_versions')
        .select('id, document_id')
        .eq('id', resolvedTargetVersionId)
        .maybeSingle();
      if (versionErr) throw new Error(versionErr.message);
      if (!versionRow?.document_id) throw new Error('Target version not found');
      resolvedTargetDocumentId = String(versionRow.document_id);
    }

    const { data: targetDocRow, error: targetErr } = await directSupabase
      .from('documents')
      .select('id, kind, organisation_id, lifecycle_ref')
      .eq('id', resolvedTargetDocumentId)
      .maybeSingle();
    if (targetErr) throw new Error(targetErr.message);
    if (!targetDocRow) throw new Error('Target document not found');
    if (targetDocRow.kind === 'Invoice') throw new Error('Cannot copy fenestration items into an invoice');
    targetDoc = targetDocRow;

    if (!resolvedTargetVersionId) {
      const active = await this.getActiveVersion(resolvedTargetDocumentId);
      if (!active?.header?.id) throw new Error('Target document has no active version');
      resolvedTargetVersionId = String(active.header.id);
    }
    if (sourceVersionId && String(resolvedTargetVersionId) === String(sourceVersionId)) {
      throw new Error('Use Clone to duplicate within the same active version.');
    }

    const { data: maxRow, error: maxErr } = await directSupabase
      .from('document_version_lines')
      .select('sort_order')
      .eq('version_id', resolvedTargetVersionId)
      .order('sort_order', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (maxErr) throw new Error(maxErr.message);
    const baseSort = Number(maxRow?.sort_order ?? -1) + 1;

    const payload = visualLines.map((line, idx) => ({
      version_id: resolvedTargetVersionId,
      line_kind: line.lineKind,
      additional_type: line.additionalType ?? null,
      material_id: line.materialId || null,
      equipment_id: line.equipmentId || null,
      labour_rate_id: line.labourRateId || null,
      name: line.name,
      description: line.description || null,
      qty: line.qty || 0,
      unit: line.unit || null,
      unit_price: line.unitPrice || 0,
      vat_inclusive: !!line.vatInclusive,
      line_total: line.lineTotal != null ? line.lineTotal : (line.qty || 0) * (line.unitPrice || 0),
      sort_order: baseSort + idx,
      labour_days: line.labourDays || null,
      labour_hours: line.labourHours || null,
      meta_json: (line as any).metaJson ?? null,
    }));

    const { error: insErr } = await directSupabase.from('document_version_lines').insert(payload);
    if (insErr) throw new Error(insErr.message);

    const { data: versionHeader, error: vhErr } = await directSupabase
      .from('document_versions')
      .select('*')
      .eq('id', resolvedTargetVersionId)
      .maybeSingle();
    if (vhErr) throw new Error(vhErr.message);
    if (!versionHeader) throw new Error('Target version not found after copy');

    const { data: versionLines, error: lErr } = await directSupabase
      .from('document_version_lines')
      .select('*')
      .eq('version_id', resolvedTargetVersionId)
      .order('sort_order', { ascending: true });
    if (lErr) throw new Error(lErr.message);

    const mappedLines: DocumentLine[] = (versionLines || []).map((line: any) => ({
      id: line.id,
      documentId: resolvedTargetDocumentId,
      lineKind: line.line_kind,
      additionalType: line.additional_type ?? null,
      materialId: line.material_id,
      equipmentId: line.equipment_id,
      labourRateId: line.labour_rate_id,
      name: line.name,
      description: line.description,
      qty: Number(line.qty ?? 0),
      unit: line.unit,
      unitPrice: Number(line.unit_price ?? 0),
      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
      vatInclusive: !!line.vat_inclusive,
      sortOrder: line.sort_order,
      labourDays: line.labour_days,
      labourHours: line.labour_hours,
      metaJson: line.meta_json ?? null,
      createdAt: line.created_at,
      updatedAt: line.updated_at,
    }));

    if (versionHeader.is_active) {
      await calculateAndStoreTotals(
        resolvedTargetDocumentId,
        mappedLines,
        {
          adminPct: Number(versionHeader.admin_pct ?? 0),
          adminCap: Number(versionHeader.admin_cap ?? 0),
          contingencyPct: Number(versionHeader.contingency_pct ?? 0),
          profitPct: Number(versionHeader.profit_pct ?? 0),
          developerPct: null,
        },
        !!versionHeader.vat_enabled,
        Number(versionHeader.vat_pct ?? 0.2)
      );

      if (targetDoc.kind === 'Quotation') {
        await documentsService.syncInvoicesForLifecycle(String(targetDoc.organisation_id), String(targetDoc.lifecycle_ref));
      }
    }

    return payload.length;
  },
};


